// planned order frontend

import React, { useState, useEffect, useMemo, useRef } from "react";
import Layout from "../../Component/Layout";
import Select from "react-select";
import { MASTER_API, PURCHASE_API } from "../../apiConfig";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CharacteristicsModal from "../../Component/CharacteristicsModal ";
import { useFieldPermissions } from "../../utils/useFieldPermissions";
import MaskedValue from "../../Component/MaskedValue";
import { FileText } from 'lucide-react';
import { authHeaders } from "../../utils/authHeaders";

// Every request must carry the JWT — the backend returns
// {"message":"No token provided"} (401) otherwise, which left the
// Workcenter dropdown empty. Caller-supplied headers (e.g. Content-Type) win.
const apiFetch = (url, options = {}) =>
    fetch(url, {
        ...options,
        headers: { ...authHeaders(), ...(options.headers || {}) },
    });


const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = ["10", "25", "50", "100", "all", "custom"];

// Reads module-level permissions (can_view / can_create / can_edit /
// can_convert) for SFC_PLANNED_ORDER from the logged-in user's stored
// permissions — same array authController.js returns at login, same
// pattern Enquiry (enquiry.js) uses for SALES_INQUIRY. Admins get an
// empty `permissions` array from the backend by design, so they're
// granted full access here rather than being locked out by an
// "empty permissions" default.
const getModulePermissions = (moduleId) => {
    try {
        const user = JSON.parse(localStorage.getItem("user"));
        if (!user) return { canView: false, canCreate: false, canEdit: false, canConvert: false };
        if (user.role === "admin") return { canView: true, canCreate: true, canEdit: true, canConvert: true };

        const perm = (user.permissions || []).find((p) => p.module_id === moduleId);
        return {
            canView: perm?.can_view === 1,
            canCreate: perm?.can_create === 1,
            canEdit: perm?.can_edit === 1,
            canConvert: perm?.can_convert === 1
        };
    } catch (e) {
        console.error("Error reading user permissions:", e);
        return { canView: false, canCreate: false, canEdit: false, canConvert: false };
    }
};

const USER_MANUAL_SCOPE = {
    moduleName: "Production",
    process: "Shop Floor Control",
    subProcess: "Planned Order"
};
const USER_MANUAL_LIST_API = `${MASTER_API}/user-manual/list`;

// ── Toast notifications (replaces window.alert) ──
// Self-contained: call toast.success/error/warning/info("message") from
// anywhere in this file; <ToastHost /> (rendered once in the page) shows them.
let toastSeq = 0;
const toastListeners = new Set();
const emitToast = (type, message) => {
    const t = { id: ++toastSeq, type, message: String(message || "") };
    toastListeners.forEach((fn) => fn(t));
};
const toast = {
    success: (m) => emitToast("success", m),
    error:   (m) => emitToast("error", m),
    warning: (m) => emitToast("warning", m),
    info:    (m) => emitToast("info", m),
};

const TOAST_COLORS = {
    success: { bg: "#ecfdf5", border: "#10b981", text: "#065f46", icon: "✓" },
    error:   { bg: "#fef2f2", border: "#ef4444", text: "#991b1b", icon: "✕" },
    warning: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e", icon: "!" },
    info:    { bg: "#eff6ff", border: "#3b82f6", text: "#1e40af", icon: "i" },
};

const ToastHost = () => {
    const [items, setItems] = useState([]);

    useEffect(() => {
        const onToast = (t) => {
            setItems((prev) => [...prev, t]);
            setTimeout(() => setItems((prev) => prev.filter((x) => x.id !== t.id)), t.type === "error" ? 6000 : 3500);
        };
        toastListeners.add(onToast);
        return () => { toastListeners.delete(onToast); };
    }, []);

    if (items.length === 0) return null;
    return (
        <div style={{ position: "fixed", top: 16, right: 16, zIndex: 100000, display: "flex", flexDirection: "column", gap: 8, maxWidth: 380 }}>
            {items.map((t) => {
                const c = TOAST_COLORS[t.type] || TOAST_COLORS.info;
                return (
                    <div key={t.id} role="status" style={{
                        display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 12px",
                        background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderLeft: `4px solid ${c.border}`,
                        borderRadius: 8, boxShadow: "0 6px 18px rgba(0,0,0,0.15)", fontSize: 13, lineHeight: 1.4,
                    }}>
                        <span style={{ fontWeight: 700, width: 18, height: 18, borderRadius: "50%", background: c.border, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, flexShrink: 0, marginTop: 1 }}>{c.icon}</span>
                        <span style={{ flex: 1 }}>{t.message}</span>
                        <button type="button" onClick={() => setItems((prev) => prev.filter((x) => x.id !== t.id))}
                            style={{ border: "none", background: "transparent", color: c.text, cursor: "pointer", fontSize: 16, lineHeight: 1, padding: 0 }}>×</button>
                    </div>
                );
            })}
        </div>
    );
};

// ── dd-mm-yyyy date field ──
// A native <input type="date"> is always drawn in the browser/OS locale
// (yyyy-mm-dd here) and can't be restyled, so this shows and accepts
// dd-mm-yyyy as text, with a calendar button that opens the native picker.
// `value` and `onChange` still use ISO yyyy-mm-dd, so callers/APIs are unchanged.
const isoToDisplay = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
    return m ? `${m[3]}-${m[2]}-${m[1]}` : "";
};

const DateField = ({ value, onChange, disabled = false }) => {
    const [text, setText] = useState(isoToDisplay(value));
    const pickerRef = useRef(null);

    // keep the text in sync when the value changes from outside (e.g. Clear Filters)
    useEffect(() => { setText(isoToDisplay(value)); }, [value]);

    const handleText = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
        let out = digits;
        if (digits.length > 4) out = `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4)}`;
        else if (digits.length > 2) out = `${digits.slice(0, 2)}-${digits.slice(2)}`;
        setText(out);

        if (digits.length === 8) {
            const d = parseInt(digits.slice(0, 2), 10);
            const m = parseInt(digits.slice(2, 4), 10);
            const y = parseInt(digits.slice(4), 10);
            const dt = new Date(y, m - 1, d);
            if (dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d) {
                onChange(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
            }
        } else if (digits.length === 0 && value) {
            onChange("");
        }
    };

    const openPicker = () => {
        const el = pickerRef.current;
        if (!el || disabled) return;
        if (typeof el.showPicker === "function") el.showPicker();
        else el.click();
    };

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <input
                type="text"
                inputMode="numeric"
                placeholder="dd-mm-yyyy"
                value={text}
                disabled={disabled}
                onChange={handleText}
                onBlur={() => setText(isoToDisplay(value))}
                style={{
                    width: "100%", height: 32, fontSize: 12, border: "1px solid #e2e8f0",
                    borderRadius: 6, padding: "0 30px 0 8px", boxSizing: "border-box",
                    color: disabled ? "#94a3b8" : "#1e293b",
                    background: disabled ? "#f1f5f9" : "#fff",
                    cursor: disabled ? "not-allowed" : "text",
                }}
            />
            <button
                type="button"
                title="Pick a date"
                disabled={disabled}
                onClick={openPicker}
                style={{
                    position: "absolute", right: 4, top: 4, width: 24, height: 24, padding: 0,
                    border: "none", background: "transparent", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={disabled ? "#cbd5e1" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                    <line x1="16" y1="2" x2="16" y2="6"></line>
                    <line x1="8" y1="2" x2="8" y2="6"></line>
                    <line x1="3" y1="10" x2="21" y2="10"></line>
                </svg>
            </button>
            {/* hidden native picker — only used to choose a date from the calendar */}
            <input
                ref={pickerRef}
                type="date"
                tabIndex={-1}
                aria-hidden="true"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            />
        </div>
    );
};

// ── HH:mm (24-hour) time field ──
// Native <input type="time"> follows the OS locale (can show AM/PM), so this
// shows and accepts 24-hour HH:mm as text. `value` / `onChange` stay "HH:mm".
const TimeField = ({ value, onChange, disabled = false }) => {
    const toDisplay = (v) => (v ? String(v).slice(0, 5) : "");
    const [text, setText] = useState(toDisplay(value));
    const pickerRef = useRef(null);
    useEffect(() => { setText(toDisplay(value)); }, [value]);

    const openPicker = () => {
        const el = pickerRef.current;
        if (!el || disabled) return;
        if (typeof el.showPicker === "function") el.showPicker();
        else el.click();
    };

    const handleText = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
        const out = digits.length > 2 ? `${digits.slice(0, 2)}:${digits.slice(2)}` : digits;
        setText(out);
        if (digits.length === 4) {
            const h = parseInt(digits.slice(0, 2), 10);
            const m = parseInt(digits.slice(2), 10);
            if (h < 24 && m < 60) onChange(`${digits.slice(0, 2)}:${digits.slice(2)}`);
        } else if (digits.length === 0 && value) {
            onChange("");
        }
    };

    return (
        <div style={{ position: "relative", width: "100%" }}>
            <input
                type="text"
                inputMode="numeric"
                placeholder="hh:mm"
                value={text}
                disabled={disabled}
                onChange={handleText}
                onBlur={() => setText(toDisplay(value))}
                style={{
                    width: "100%", height: 32, fontSize: 12, border: "1px solid #e2e8f0",
                    borderRadius: 6, padding: "0 30px 0 8px", boxSizing: "border-box",
                    color: disabled ? "#94a3b8" : "#1e293b",
                    background: disabled ? "#f1f5f9" : "#fff",
                    cursor: disabled ? "not-allowed" : "text",
                }}
            />
            <button
                type="button"
                title="Pick a time"
                disabled={disabled}
                onClick={openPicker}
                style={{
                    position: "absolute", right: 4, top: 4, width: 24, height: 24, padding: 0,
                    border: "none", background: "transparent", display: "flex",
                    alignItems: "center", justifyContent: "center",
                    cursor: disabled ? "not-allowed" : "pointer",
                }}
            >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={disabled ? "#cbd5e1" : "#64748b"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"></circle>
                    <polyline points="12 6 12 12 16 14"></polyline>
                </svg>
            </button>
            {/* hidden native time picker — only used to choose a time from the clock */}
            <input
                ref={pickerRef}
                type="time"
                tabIndex={-1}
                aria-hidden="true"
                value={toDisplay(value)}
                onChange={(e) => onChange(e.target.value)}
                style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            />
        </div>
    );
};

const PlannedOrder = () => {
    const location = useLocation();
    const navigate = useNavigate();
    
    const [showManual, setShowManual] = useState(false);
    const [manualExists, setManualExists] = useState(null);
    const [manualFile, setManualFile] = useState(null);

    // Module-level access for this page.
    //  - canView    -> DISPLAY/CHANGE/REVIEW: can browse/view the list & modal
    //  - canCreate  -> CREATE: sees the "Create" button / create modal
    //  - canEdit    -> CHANGE: Order Qty field is editable in the view modal
    //  - canConvert -> CONVERT: sees row checkboxes + the Convert button
    // A Convert-only role has no can_view of its own, but still needs to see
    // the list to pick rows from — so the list itself is gated on
    // (canView || canConvert), same idea as CREATE-only being add-only but
    // still needing the page to reach the create form.
    const { canView, canCreate, canEdit, canConvert } = useMemo(
        () => getModulePermissions("SFC_PLANNED_ORDER"),
        []
    );
    const canSeePage = canView || canCreate || canConvert;

    // Field-level access for this module. isFieldEditable('qty') follows a
    // CHANGE-role's saved checklist for the Order Qty field; falls open
    // (true) for admins, users with no role assigned yet, or any
    // non-CHANGE permission type — matching useFieldPermissions' own
    // documented behavior.
    const { isFieldVisible, isFieldEditable } = useFieldPermissions("SFC_PLANNED_ORDER");
    const isQtyFieldLocked = !isFieldEditable("qty");

    // ── Filter state ──
    const [listingFilters, setListingFilters] = useState({
        construction: null, code: null, cableType: null, category: null,
        noOfCores: null, color: null, nominalCrossSection: null,
        referenceOfStandard: null, voltage: null,
    });
    const [selectedProductCode, setSelectedProductCode] = useState(null);
    const [selectedWorkcenter, setSelectedWorkcenter] = useState(null);
    const [productCodeMasterOptions, setProductCodeMasterOptions] = useState([]);
    const [workcenterMasterOptions, setWorkcenterMasterOptions] = useState([]);
    const [poFilterMasterData, setPoFilterMasterData] = useState({
        productTypes: [], constructions: [], codes: [], cableTypes: [],
        categories: [], cores: [], colours: [], nominalCrossSections: [],
        referenceOdStandards: [], voltages: [],
    });
    const [selectedProductType, setSelectedProductType] = useState(null);
    const [selectedPlannedOrderNo, setSelectedPlannedOrderNo] = useState("");
    const [plannedOrderNumbers, setPlannedOrderNumbers] = useState([]);
    const [selectedSalesOrderNo, setSelectedSalesOrderNo] = useState("");
    const [salesOrderNumbers, setSalesOrderNumbers] = useState([]);
    const [selectedFromDate, setSelectedFromDate] = useState("");
    const [selectedToDate, setSelectedToDate] = useState("");
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    // Tracks which row's delete request is in flight, so the dustbin icon
    // can show a spinner/disable itself for just that row while it deletes.
    const [deletingOrderNo, setDeletingOrderNo] = useState(null);

    // ── Active / Posted toggle ──
    const [activeTab, setActiveTab] = useState("active");

    // ── Pagination ──
    const [currentPage, setCurrentPage] = useState(1);
    // Rows per page: "10" | "25" | "50" | "100" | "all" | "custom"
    const [pageSizeOption, setPageSizeOption] = useState("10");
    const [customPageSize, setCustomPageSize] = useState("");
    const [selectedRows, setSelectedRows] = useState([]);

    // ── Convert state ──
    const [converting, setConverting] = useState(false);
    const [convertResult, setConvertResult] = useState(null);

    // ── Create Planned Order (manual) Modal State ──
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createLoadingBom, setCreateLoadingBom] = useState(false);
    //eslint-disable-next-line
    const [createActiveTab, setCreateActiveTab] = useState("");
    const [createForm, setCreateForm] = useState({
        productCode: null,     // { value, label }
        description: "",
        uom: "",
        productType: "",
        quantity: "",
        altBom: null,          // { value, label }
        baseUnit: "",
        requirementDate: "",
        // ── auto-filled spec strip (from product master, same as view mode) ──
        construction: "", code: "", cableType: "", productCategory: "",
        noOfCores: "", color: "", nominalCrossSection: "",
        referenceOfStandard: "", voltage: "",
        // ── Sales Order row (optional, same as Production Order create) ──
        salesOrder: null,      // { value, label } react-select
        item: null,            // { value, label } react-select
        customer: "", purchaseOrder: "", soQty: "",
        // ── production schedule (editable in create, same as Production Order) ──
        startDate: "", startTime: "", endDate: "", endTime: "",
        // ── Order Qty characteristics (editable in create mode) ──
        orderQtyChars: [],
        // ── Special Remarks (optional free text) ──
        specialRemarks: "",
    });
    const [createAltBomOptions, setCreateAltBomOptions] = useState([]);
    const [createComponents, setCreateComponents] = useState([]);   // BOM-exploded, from bom table
    const [createOperations, setCreateOperations] = useState([]);   // from routing_activity
    const [allProductCodesData, setAllProductCodesData] = useState([]); // raw product master rows for spec auto-fill lookup
    const [createSalesOrderOptions, setCreateSalesOrderOptions] = useState([]);
    const [createSoItemOptions, setCreateSoItemOptions] = useState([]);
    //eslint-disable-next-line
    const [createCharacteristicsConfig, setCreateCharacteristicsConfig] = useState([]); // [{characteristic, calculation_type}]
    const [showCreateClassModal, setShowCreateClassModal] = useState(false);

    // ── View Modal State ──
    const [showViewModal, setShowViewModal] = useState(false);
    const [viewModalData, setViewModalData] = useState({});
    const [viewComponents, setViewComponents] = useState([]);
    const [viewMachineTime, setViewMachineTime] = useState([]);
    const [showClassModal, setShowClassModal] = useState(false);
    //eslint-disable-next-line no-unused-vars
    const [viewCharacteristics, setViewCharacteristics] = useState([]);
    // which characteristics list to show: "orderQty" | "soQty"
    const [activeCharType, setActiveCharType] = useState("orderQty");
    // all Sales Order lines this planned order is pegged against (view mode table)
    const [viewSalesOrderRows, setViewSalesOrderRows] = useState([]);
    // which specific SO line's own characteristics are showing in the ▶ popup
    const [activeSoCharRow, setActiveSoCharRow] = useState(null);

    // ── Order Qty inline-edit state ──
    const [originalQty, setOriginalQty] = useState("");
    const [updatingQty, setUpdatingQty] = useState(false);
    // Special Remarks as last loaded from the DB (to detect edits in view mode)
    const [originalSpecialRemarks, setOriginalSpecialRemarks] = useState("");

    // Accordion State
    const [expandedSections, setExpandedSections] = useState({
        material_details: true,
        machine_time: false,
        special_remarks: false
    });

    const toggleSection = (key) => {
        setExpandedSections(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // ══════════════════════════════════════════════════════════
    // getCurrentUserId
    // Returns the logged-in user's ID (not their email/name), for
    // fields like createdBy on Planned Order creation.
    // ══════════════════════════════════════════════════════════
    const getCurrentUserId = () => {
        const userObj = localStorage.getItem("user");
        if (userObj) {
            try {
                const parsedUser = JSON.parse(userObj);
                const id = parsedUser.userId ?? parsedUser.user_id ?? parsedUser.id;
                if (id !== undefined && id !== null && id !== "") return id;
            } catch (e) {
                console.error("Error parsing user data:", e);
            }
        }
        const storedUserId = localStorage.getItem("userId");
        if (storedUserId && storedUserId !== "undefined" && storedUserId !== "null") return storedUserId;
        return null;
    };

    // ── Fetch planned orders ──
    const fetchPlannedOrders = async ({ plannedOrderNo = "", fromDate = "", toDate = "", salesOrderNo = "" } = {}) => {
        setLoading(true);
        setCurrentPage(1);
        setSelectedRows([]);
        try {
            const params = new URLSearchParams();
            if (plannedOrderNo) params.append("plannedOrderNo", plannedOrderNo);
            if (fromDate) params.append("fromDate", fromDate);
            if (toDate) params.append("toDate", toDate);
            if (salesOrderNo) params.append("salesOrderNo", salesOrderNo);
            const url = `${PURCHASE_API}/planned-order/list${params.toString() ? `?${params}` : ""}`;
            const res = await apiFetch(url);
            const data = res.ok ? await res.json() : [];
            setOrders(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error("Failed fetching planned orders", err);
            setOrders([]);
        } finally {
            setLoading(false);
        }
    };

    // Soft-delete: flips pl_header.is_deleted to 1 (row is kept for audit),
    // drops the order out of the list/dropdowns, deletes its
    // planning_pegging_lock rows and nulls out its `pegging` rows via
    // DELETE /planned-order/:plannedOrderNo -> service.deletePlannedOrder.
    // Callable from the table row's dustbin icon (same pattern as
    // Production Order's handleDeleteProductionOrder).
    const handleDeletePlannedOrder = async (plannedOrderNo) => {
        if (!plannedOrderNo) return;

        if (!window.confirm(`Delete Planned Order ${plannedOrderNo}? This cannot be undone.`)) {
            return;
        }

        setDeletingOrderNo(plannedOrderNo);
        try {
            const res = await apiFetch(`${PURCHASE_API}/planned-order/${plannedOrderNo}`, {
                method: "DELETE",
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok || data?.success === false) {
                throw new Error(data?.error || "Failed to delete order");
            }

            toast.success(`Planned Order ${plannedOrderNo} deleted.`);

            await fetchPlannedOrders({
                plannedOrderNo: selectedPlannedOrderNo,
                fromDate: selectedFromDate,
                toDate: selectedToDate,
                salesOrderNo: selectedSalesOrderNo,
            });
        } catch (err) {
            console.error("Delete failed", err);
            toast.error(err?.message || "Failed to delete order");
        } finally {
            setDeletingOrderNo(null);
        }
    };

    useEffect(() => {
        const fetchFilterMasterData = async () => {
            try {
                const [
                    resProdTypes, resCons, resCode, resCable, resCat,
                    resCore, resCol, resCross, resRef, resVolt
                ] = await Promise.all([
                    apiFetch(`${MASTER_API}/configuration/product-types`),
                    apiFetch(`${MASTER_API}/configuration/construction`),
                    apiFetch(`${MASTER_API}/configuration/dynamic/code`),
                    apiFetch(`${MASTER_API}/configuration/cable-type`),
                    apiFetch(`${MASTER_API}/configuration/category`),
                    apiFetch(`${MASTER_API}/configuration/core`),
                    apiFetch(`${MASTER_API}/configuration/colour`),
                    apiFetch(`${MASTER_API}/configuration/dynamic/nominal-cross-section`),
                    apiFetch(`${MASTER_API}/configuration/dynamic/reference-of-standard`),
                    apiFetch(`${MASTER_API}/configuration/dynamic/voltage`),
                ]);
                const prodTypesData = resProdTypes.ok ? await resProdTypes.json() : [];
                setPoFilterMasterData({
                    productTypes: prodTypesData.map(item => ({ value: item.name, label: item.name })).filter(o => o.value),
                    constructions: resCons.ok ? await resCons.json() : [],
                    codes: resCode.ok ? await resCode.json() : [],
                    cableTypes: resCable.ok ? await resCable.json() : [],
                    categories: resCat.ok ? await resCat.json() : [],
                    cores: resCore.ok ? await resCore.json() : [],
                    colours: resCol.ok ? await resCol.json() : [],
                    nominalCrossSections: resCross.ok ? await resCross.json() : [],
                    referenceOdStandards: resRef.ok ? await resRef.json() : [],
                    voltages: resVolt.ok ? await resVolt.json() : [],
                });
            } catch (err) {
                console.error("Failed fetching filter master data", err);
            }
        };

        const fetchPlannedOrderNumbers = async () => {
            try {
                const res = await apiFetch(`${PURCHASE_API}/planned-order/planned-order-numbers`);
                const data = res.ok ? await res.json() : [];
                setPlannedOrderNumbers(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed fetching planned order numbers", err);
            }
        };

        const fetchSalesOrderNumbers = async () => {
            try {
                const res = await apiFetch(`${PURCHASE_API}/planned-order/sales-order-numbers`);
                const data = res.ok ? await res.json() : [];
                setSalesOrderNumbers(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed fetching sales order numbers", err);
            }
        };

        const fetchProductCodeAndWorkcentreMasterData = async () => {
            try {
                // Fetching exactly how ProductionOrder.js handles it
                const [resCodes, resWc] = await Promise.all([
                    apiFetch(`${PURCHASE_API}/production-order/list`),
                    apiFetch(`${MASTER_API}/workcenter`), // Singular endpoint
                ]);

                const codesData = resCodes.ok ? await resCodes.json() : [];
                setAllProductCodesData(Array.isArray(codesData) ? codesData : []);

                // Handle the nested data structure from MASTER_API
                const wcRes = resWc.ok ? await resWc.json() : {};
                const wcData = wcRes.data || wcRes || [];

                setProductCodeMasterOptions(
                    (Array.isArray(codesData) ? codesData : []).map(item => {
                        const code = String(item.productCode || "").trim();
                        const desc = String(item.description || "").trim();

                        return {
                            value: code,
                            // This exactly matches the logic in ProductionOrder.js
                            label: desc ? `${code} — ${desc}` : code
                        };
                    }).filter(opt => opt.value !== "")
                );

                setWorkcenterMasterOptions(
                    (Array.isArray(wcData) ? wcData : []).map(item => {
                        // ProductionOrder.js maps 'workcenter_name'
                        const wc = String(item.workcenter_name || "").trim();
                        return { value: wc, label: wc };
                    }).filter(opt => opt.value !== "")
                );
            } catch (err) {
                console.error("Failed fetching product code / workcentre master data", err);
            }
        };

        const fetchCreateSalesOrderOptions = async () => {
            try {
                const res = await apiFetch(`${PURCHASE_API}/production-order/sales-orders`);
                const data = res.ok ? await res.json() : [];
                setCreateSalesOrderOptions(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Failed fetching sales order options", err);
            }
        };

        fetchFilterMasterData();
        fetchPlannedOrderNumbers();
        fetchSalesOrderNumbers();
        fetchProductCodeAndWorkcentreMasterData();
        fetchCreateSalesOrderOptions();
        fetchPlannedOrders();
    }, []);

    useEffect(() => {
        setCurrentPage(1);
        setSelectedRows([]);
    }, [activeTab]);

    // ── Row selection ──
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const selectableIndices = pagedOrders
                .map((o, i) => ({ o, globalIdx: (safeCurrentPage - 1) * pageSize + i }))
                .filter(({ o }) => !o.is_converted)
                .map(({ globalIdx }) => globalIdx);
            setSelectedRows(selectableIndices);
        } else {
            setSelectedRows([]);
        }
    };

    const handleRowSelect = (index, isConverted) => {
        if (isConverted) return;
        setSelectedRows(prev =>
            prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
        );
    };

    const handleListingFilter = (key, selectedOpt) => {
        setListingFilters(prev => ({ ...prev, [key]: selectedOpt }));
        setCurrentPage(1);
    };

    const EMPTY_LISTING_FILTERS = {
        construction: null, code: null, cableType: null, category: null,
        noOfCores: null, color: null, nominalCrossSection: null,
        referenceOfStandard: null, voltage: null,
    };

    // True when any filter on this page is currently applied
    const hasActiveFilters =
        !!selectedProductType ||
        !!selectedProductCode ||
        !!selectedWorkcenter ||
        !!selectedPlannedOrderNo ||
        !!selectedSalesOrderNo ||
        !!selectedFromDate ||
        !!selectedToDate ||
        Object.values(listingFilters).some(Boolean);

    // Resets every filter and reloads the unfiltered list
    const handleClearFilters = () => {
        setListingFilters({ ...EMPTY_LISTING_FILTERS });
        setSelectedProductType(null);
        setSelectedProductCode(null);
        setSelectedWorkcenter(null);
        setSelectedPlannedOrderNo("");
        setSelectedSalesOrderNo("");
        setSelectedFromDate("");
        setSelectedToDate("");
        setCurrentPage(1);
        setSelectedRows([]);
        fetchPlannedOrders();
    };

    const getPoFilterOptions = (list) => {
        if (!list || !Array.isArray(list)) return [];
        return list.map(item => {
            if (typeof item === "string") return { value: item, label: item };
            const val = item.name || item.code || item.cable_type || item.category
                || item.core || item.colour || item.value || "";
            return { value: String(val).trim(), label: String(val).trim() };
        }).filter(opt => opt.value !== "");
    };

    const handlePlannedOrderNoChange = (opt) => {
        const val = opt ? opt.value : "";
        setSelectedPlannedOrderNo(val);
        fetchPlannedOrders({ plannedOrderNo: val, fromDate: selectedFromDate, toDate: selectedToDate, salesOrderNo: selectedSalesOrderNo });
    };

    const handleSalesOrderNoChange = (opt) => {
        const val = opt ? opt.value : "";
        setSelectedSalesOrderNo(val);
        fetchPlannedOrders({ plannedOrderNo: selectedPlannedOrderNo, fromDate: selectedFromDate, toDate: selectedToDate, salesOrderNo: val });
    };

    const handleFromDateChange = (e) => {
        const val = e.target.value;
        setSelectedFromDate(val);
        if (!val) {
            setSelectedToDate("");
            fetchPlannedOrders({ plannedOrderNo: selectedPlannedOrderNo, salesOrderNo: selectedSalesOrderNo });
        } else {
            fetchPlannedOrders({ plannedOrderNo: selectedPlannedOrderNo, fromDate: val, toDate: selectedToDate, salesOrderNo: selectedSalesOrderNo });
        }
    };

    const handleToDateChange = (e) => {
        const val = e.target.value;
        setSelectedToDate(val);
        fetchPlannedOrders({ plannedOrderNo: selectedPlannedOrderNo, fromDate: selectedFromDate, toDate: val, salesOrderNo: selectedSalesOrderNo });
    };

    // Converts any incoming date (yyyymmdd, yyyy-mm-dd, or ISO with time) to dd-mm-yyyy
    const formatDate = (raw) => {
        if (!raw) return "";
        let s = String(raw).trim();

        // Strip time portion if present (e.g. "2026-07-20T00:00:00")
        if (s.includes("T")) s = s.substring(0, 10);

        // yyyy-mm-dd
        let match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (match) {
            const [, yyyy, mm, dd] = match;
            return `${dd}-${mm}-${yyyy}`;
        }

        // yyyymmdd (plain, no separators)
        match = s.match(/^(\d{4})(\d{2})(\d{2})$/);
        if (match) {
            const [, yyyy, mm, dd] = match;
            return `${dd}-${mm}-${yyyy}`;
        }

        // Fallback: already dd-mm-yyyy or unrecognized format, return as-is
        return s;
    };

    // ══════════════════════════════════════════════════════════
    // VIEW MODAL LOGIC
    // ══════════════════════════════════════════════════════════
    const openViewModal = async (plannedOrderNo) => {
        try {
            const res = await apiFetch(`${PURCHASE_API}/planned-order/details/${plannedOrderNo}`);
            const data = res.ok ? await res.json() : [];

            const scheduleRes = await apiFetch(`${PURCHASE_API}/planned-order/schedule/${plannedOrderNo}`);
            const scheduleData = scheduleRes.ok ? await scheduleRes.json() : [];

            if (data && data.length > 0) {
                const header = data.find(d => d.pl_number === plannedOrderNo && (d.level === 0 || d.level === null)) || data[0];
                const components = data.filter(d => d.id !== header.id);

                const startDate = scheduleData.length > 0 ? scheduleData[0].start_date : "";
                const startTime = scheduleData.length > 0 ? scheduleData[0].start_time : "";
                const endDate = scheduleData.length > 0 ? scheduleData[scheduleData.length - 1].end_date : "";
                const endTime = scheduleData.length > 0 ? scheduleData[scheduleData.length - 1].end_time : "";

                const extra = header._extra || {};

                setViewModalData({
                    plannedOrderNo: header.pl_number,
                    productType: header.product_type || "",
                    construction: header.construction || "",
                    code: header.code || "",
                    cableType: header.cable_type || "",
                    productCategory: header.product_category || "",
                    noOfCores: header.no_of_cores || "",
                    color: header.color || "",
                    nominalCrossSection: header.nominal_cross_section || "",
                    referenceOfStandard: header.reference_of_standard || "",
                    voltage: header.voltage || "",
                    productCode: header.product_code || "",
                    description: header.description || "",
                    uom: header.uom || "",
                    qty: header.qty || "",
                    isConverted: Number(header.is_converted) === 1,
                    startDate,
                    startTime,
                    endDate,
                    endTime,
                    reqDate: header.requirement_date || "",
                    // Who actually created this planned order, as stored in the DB —
                    // NOT the currently logged-in user viewing the modal. MRP-generated
                    // rows (from planning_elements) have no created_by, so fall back to
                    // a neutral label instead of silently showing the viewer's own name.
                    // Prefer the real value from the DB (who created / converted it).
                    // Only fall back to the current logged-in user's email if the
                    // backend genuinely has nothing stored for this order.
                    createdBy: header.created_by || "",
                    createdOn: header.created_on || "",
                    // ── from routings_data ──
                    altBom: extra.altBom || "",
                    baseUnit: extra.baseUnit || "",
                    // ── from planning_sales_order → SO (first line, kept for
                    // backward compat -- the full set is in viewSalesOrderRows) ──
                    salesOrder: extra.salesOrderNo || "",
                    item: extra.soItemSeq != null ? String(extra.soItemSeq) : "",
                    customer: extra.customer || "",
                    purchaseOrder: extra.purchaseOrder || "",
                    soQty: extra.soQty || "",
                    // ── characteristics (two sets) ──
                    orderQtyChars: extra.orderQtyChars || [],
                    soQtyChars: extra.soQtyChars || [],
                    // class_details falls back to orderQtyChars for CharacteristicsModal
                    class_details: extra.orderQtyChars || [],
                    // ── optional free-text remarks (pl_header.special_remarks) ──
                    specialRemarks: header.special_remarks || "",
                });

                // Every Sales Order line this planned order is pegged against
                // (a planned order can be split across more than one SO line) --
                // rendered as a table, same as the Production Order view mode.
                setViewSalesOrderRows(extra.soLines || []);

                const mappedComps = components.map((c, i) => ({
                    item: i + 1,
                    product_type: c.product_type || "",
                    product_code: c.product_code,
                    description: c.description,
                    quantity: c.qty,
                    total_qty: c.req_rec_qty,
                    uom: c.uom,
                }));

                setViewComponents(mappedComps);
                setViewMachineTime(scheduleData);
                setViewCharacteristics([]);
                // Auto-open the Special Remarks accordion only when there is something in it
                setExpandedSections({
                    material_details: true,
                    machine_time: false,
                    special_remarks: !!String(header.special_remarks || "").trim(),
                });
                setOriginalQty(header.qty || "");
                setOriginalSpecialRemarks(header.special_remarks || "");
                setShowViewModal(true);
            } else {
                toast.warning("No details found for this planned order.");
            }
        } catch (e) {
            console.error("Failed to fetch planned order details", e);
            toast.error("Failed to fetch planned order details.");
        }
    };

    // Auto-open modal if navigated from Product Planning
    useEffect(() => {
        const returnPlannedOrderNo =
            location.state?.openPlannedOrderNo ||
            location.state?.returnParams?.openPlannedOrderNo;
        if (returnPlannedOrderNo) {
            openViewModal(returnPlannedOrderNo);
            window.history.replaceState({}, document.title);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Sales Order number in the view modal -> open that SO in the Sales Order
    // page. SalesOrder.js opens the SO from location.state.soId and, when its
    // modal is closed, navigates back to `returnTo` with `returnParams`; the
    // auto-open effect above then re-opens this planned order.
    const handleOpenSalesOrder = (soNumber, plannedOrderNo) => {
        if (!soNumber) return;
        navigate("/cable/sales/sales-order", {
            state: {
                soId: soNumber,
                returnTo: location.pathname + location.search,
                returnParams: { openPlannedOrderNo: plannedOrderNo },
            },
        });
    };

    const handleCloseModal = () => {
        setShowViewModal(false);
        if (location.state?.returnTo) {
            navigate(location.state.returnTo, {
                state: { returnParams: location.state.returnParams }
            });
        }
    };

    // ── Save edited Order Qty and/or Special Remarks ──
    // Order Qty: updates planning_elements.qty, planning_element_schedule.qty,
    // and pegging.child_qty (all matched on the planned order number).
    // Special Remarks (optional): updates pl_header.special_remarks.
    // Only the parts that actually changed are sent; then the modal + list are
    // refreshed so everything stays in sync.
    const handleSaveChanges = async () => {
        const plannedOrderNo = viewModalData.plannedOrderNo;
        const doQty = qtyChanged && !isQtyFieldLocked;
        const doRemarks = remarksChanged;
        if (!doQty && !doRemarks) return; // nothing changed

        const newQty = Number(viewModalData.qty);
        if (doQty && (!newQty || newQty <= 0 || isNaN(newQty))) {
            toast.warning("Please enter a valid Order Qty greater than 0.");
            return;
        }

        setUpdatingQty(true);
        try {
            // 1) Special Remarks (optional — may be saved empty to clear it)
            if (doRemarks) {
                const res = await apiFetch(`${PURCHASE_API}/planned-order/special-remarks/${encodeURIComponent(plannedOrderNo)}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        specialRemarks: (viewModalData.specialRemarks || "").trim(),
                        updatedBy: getCurrentUserId(),
                    }),
                });
                const data = await res.json().catch(() => ({}));
                if (!res.ok || !data.success) {
                    toast.error(data.error || "Failed to save Special Remarks.");
                    return;
                }
                setOriginalSpecialRemarks((viewModalData.specialRemarks || "").trim());
                if (!doQty) toast.success("Special Remarks saved.");
            }

            // 2) Order Qty
            if (doQty) {
                const res = await apiFetch(`${PURCHASE_API}/planned-order/update-qty/${encodeURIComponent(plannedOrderNo)}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        qty: newQty,
                        updatedBy: getCurrentUserId(),
                        // Only sent for Finished Goods, where qty was derived
                        // from the Order Qty Characteristics modal — persists
                        // those entered values so reopening this order shows
                        // what was just typed, not the stale creation-time ones.
                        ...(viewModalData.orderQtyChars?.length ? { characteristics: viewModalData.orderQtyChars } : {}),
                    }),
                });
                const data = await res.json().catch(() => ({}));

                if (!res.ok || !data.success) {
                    toast.error(data.error || "Failed to update Order Qty.");
                    return;
                }
                setOriginalQty(String(newQty));
            }

            await openViewModal(plannedOrderNo);
            await fetchPlannedOrders({
                plannedOrderNo: selectedPlannedOrderNo,
                fromDate: selectedFromDate,
                toDate: selectedToDate,
                salesOrderNo: selectedSalesOrderNo,
            });
        } catch (err) {
            console.error("Failed to save planned order changes", err);
            toast.error("Failed to save changes.");
        } finally {
            setUpdatingQty(false);
        }
    };

    const qtyChanged = String(viewModalData.qty ?? "") !== String(originalQty ?? "") &&
        viewModalData.qty !== "" && viewModalData.qty !== null && viewModalData.qty !== undefined;

    const remarksChanged =
        String(viewModalData.specialRemarks ?? "").trim() !== String(originalSpecialRemarks ?? "").trim();

    // ══════════════════════════════════════════════════════════
    // REUSABLE COMPONENTS FOR MODAL
    // ══════════════════════════════════════════════════════════
    const ComponentsTable = ({ rows, isCreate }) => {
        if (!rows || rows.length === 0) return (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#94a3b8', fontSize: 13 }}>
                No components found.
            </div>
        );

        const thStyle = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 10px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
        const tdStyle = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" };

        return (
            <div style={{ marginTop: 10, overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: 600 }}>
                    <colgroup>
                        <col style={{ width: 45 }} />
                        <col style={{ width: 110 }} />
                        <col style={{ width: 145 }} />
                        <col style={{ width: "40%" }} />
                        <col style={{ width: 85 }} />
                        <col style={{ width: 55 }} />
                        <col style={{ width: 85 }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th style={{ ...thStyle, textAlign: "center" }}>Item</th>
                            <th style={thStyle}>Product Type</th>
                            <th style={thStyle}>Product Code</th>
                            <th style={thStyle}>Description</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>Total Qty</th>
                            <th style={thStyle}>UOM</th>
                            <th style={thStyle}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => {
                            const totalQty = Number(row.total_qty || 0).toFixed(2);
                            return (
                                <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }} onMouseEnter={e => e.currentTarget.style.background = "#f1f5f9"} onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}>
                                    <td style={{ ...tdStyle, textAlign: "center", fontWeight: 600, color: "#475569" }}>{row.item}</td>
                                    <td style={tdStyle}>{row.product_type || ""}</td>
                                    <td style={{ ...tdStyle, fontWeight: 500, color: "#2563eb" }}>
                                        {!isCreate && !isFieldVisible('component_product_code') ? <MaskedValue height={22} /> : (row.product_code || "")}
                                    </td>
                                    <td style={{ ...tdStyle, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={row.description}>
                                        {!isCreate && !isFieldVisible('component_description') ? <MaskedValue height={22} /> : row.description}
                                    </td>
                                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#0f172a" }}>
                                        {!isCreate && !isFieldVisible('component_total_qty') ? <MaskedValue height={22} /> : totalQty}
                                    </td>
                                    <td style={tdStyle}>
                                        {!isCreate && !isFieldVisible('component_uom') ? <MaskedValue height={22} /> : row.uom}
                                    </td>
                                    <td style={tdStyle}></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    };

    const OperationsTable = ({ rows, qty, baseUnit, editable, onRowChange }) => {
        if (!rows || rows.length === 0) return (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#94a3b8", fontSize: 13 }}>
                No machine time/operations data available for Planned Orders.
            </div>
        );

        const thStyle = { background: "#f8fafc", fontSize: 12, fontWeight: 600, padding: "9px 10px", borderBottom: "2px solid #e5e7eb", color: "#1e293b", whiteSpace: "nowrap", textAlign: "left" };
        const tdStyle = { fontSize: 12, padding: "8px 10px", borderBottom: "1px solid #f1f5f9", color: "#334155", verticalAlign: "middle" };

        const bUnit = Number(baseUnit) || 1;
        const prodQty = Number(qty) || 1;

        // Setup is fixed; Run Time is scaled: (run_time * qty) / baseUnit
        const calcRunTime = (rawRunTime) => {
            const val = parseFloat(rawRunTime);
            if (isNaN(val)) return "";
            return ((val * prodQty) / bUnit).toFixed(2);
        };

        return (
            <div style={{ marginTop: 10, overflowX: "auto", borderRadius: 8, border: "1px solid #e5e7eb", background: "#fff" }}>
                {editable && (
                    <div style={{ padding: "8px 10px", fontSize: 11.5, color: "#64748b", borderBottom: "1px solid #f1f5f9", background: "#fafbfc" }}>
                        
                    </div>
                )}
                <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 600 }}>
                    <thead>
                        <tr>
                            <th style={thStyle}>Process</th>
                            <th style={thStyle}>Workcenter</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>Setup</th>
                            <th style={{ ...thStyle, textAlign: "right" }}>Run Time</th>
                            <th style={thStyle}>UOM</th>
                            <th style={thStyle}>Start Date</th>
                            <th style={thStyle}>Start Time</th>
                            <th style={thStyle}>End Date</th>
                            <th style={thStyle}>End Time</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, idx) => (
                            <tr key={idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc" }}>
                                <td style={tdStyle}>{row.process_name || ""}</td>
                                <td style={tdStyle}>
                                    {!editable && !isFieldVisible('operation_workcenter') ? <MaskedValue height={22} /> : (row.workcenter_name || "")}
                                </td>
                                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>{row.setup || ""}</td>
                                <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600 }}>
                                    {row.run_time != null && row.run_time !== "" ? calcRunTime(row.run_time) : ""}
                                </td>
                                <td style={tdStyle}>{row.run_time || row.setup ? "Min" : ""}</td>
                                {editable ? (
                                    <>
                                        <td style={tdStyle}>
                                            <DateField
                                                value={row.start_date || ""}
                                                onChange={(v) => onRowChange(idx, "start_date", v)} />
                                        </td>
                                        <td style={tdStyle}>
                                            <TimeField
                                                value={row.start_time || ""}
                                                onChange={(v) => onRowChange(idx, "start_time", v)} />
                                        </td>
                                    </>
                                ) : (
                                    <>
                                        <td style={tdStyle}>
                                            {!isFieldVisible('operation_start') ? <MaskedValue height={22} /> : formatDate(row.start_date)}
                                        </td>
                                        <td style={tdStyle}>
                                            {!isFieldVisible('operation_start') ? <MaskedValue height={22} /> : (row.start_time || "")}
                                        </td>
                                    </>
                                )}
                                <td style={tdStyle}>
                                    {!editable && !isFieldVisible('operation_end')
                                        ? <MaskedValue height={22} />
                                        : formatDate(row.end_date)}
                                </td>
                                <td style={tdStyle}>
                                    {!editable && !isFieldVisible('operation_end') ? <MaskedValue height={22} /> : (row.end_time || "")}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    // ══════════════════════════════════════════════════════════
    // SHARED MODAL — used for BOTH "View Planned Order" and
    // "Create Planned Order". Same layout every time; only a handful
    // of fields switch between editable (create) and read-only (view).
    // Defined inline here (not a separate file) so it closes over all
    // the component's state/handlers directly — no giant prop list.
    // ══════════════════════════════════════════════════════════
    const PlannedOrderModal = ({ mode }) => {
        const isCreate = mode === "create";

        const salesOrderRows = isCreate
            ? []
            : (viewSalesOrderRows.length > 0 ? viewSalesOrderRows : [{
                soNumber: viewModalData.salesOrder,
                soItemSeq: viewModalData.item,
                customer: viewModalData.customer,
                purchaseOrder: viewModalData.purchaseOrder,
                productCode: viewModalData.productCode || viewModalData.product_code,
                productDescription: viewModalData.productDescription || viewModalData.product_description,
                reqDate: formatDate(viewModalData.reqDate),
                soQty: viewModalData.soQty,
                soQtyChars: viewModalData.soQtyChars || [],
            }]);

        // In view mode, Order Qty is read-only if already converted, OR the
        // logged-in user's role doesn't grant edit access to it at all
        // (no CHANGE-type role for this module) OR a CHANGE-type role has
        // this specific field unchecked in Role.js.
        // ── FG vs others — Order Qty entry mode ──
        // Mirrors Production Order's ProductionOrder.js `isFinished` check:
        // for Finished Goods, Order Qty is derived from the Order Qty
        // Characteristics modal (No. of Drum/Coil × Length of the Drum/Coil)
        // and the header field itself is locked; for every other product
        // type the header field stays a plain, directly-editable number.
        const qtyProductType = (isCreate ? createForm.productType : viewModalData.productType) || "";
        const isFinishedGood = ["finished", "finished goods"].includes(qtyProductType.toString().toLowerCase().trim());
        const canEditQtyChars = isFinishedGood && canEdit && !isQtyFieldLocked && !viewModalData.isConverted;
        const qtyReadOnly = isCreate
            ? isFinishedGood
            : (!!viewModalData.isConverted || !canEdit || isQtyFieldLocked || isFinishedGood);
        const qtyValue = isCreate ? createForm.quantity : (viewModalData.qty ?? "");
        const onQtyChange = isCreate
            ? (e) => setCreateForm(prev => ({ ...prev, quantity: e.target.value }))
            : (e) => setViewModalData(prev => ({ ...prev, qty: e.target.value }));

        // ── Special Remarks (optional) ──
        // Editable in create mode; in view mode editable until the order is
        // converted (then it's frozen, same as Order Qty) or if the user can't edit.
        const remarksReadOnly = !isCreate && (!!viewModalData.isConverted || !canEdit);
        const remarksValue = isCreate ? (createForm.specialRemarks || "") : (viewModalData.specialRemarks ?? "");
        const onRemarksChange = isCreate
            ? (e) => setCreateForm(prev => ({ ...prev, specialRemarks: e.target.value }))
            : (e) => setViewModalData(prev => ({ ...prev, specialRemarks: e.target.value }));

        const componentsRows = isCreate ? createComponentsWithTotals : viewComponents;
        const operationsRows = isCreate ? createOperationsForDisplay : viewMachineTime;
        const operationsQty = isCreate ? createOrderQty : viewModalData.qty;
        const operationsBaseUnit = isCreate ? createBaseUnit : viewModalData.baseUnit;

        const onClose = isCreate ? closeCreateModal : handleCloseModal;
        const showSubmitButton = isCreate
            ? true
            : (!viewModalData.isConverted && canEdit && ((qtyChanged && !isQtyFieldLocked) || remarksChanged));
        const onSubmit = isCreate ? handleCreateSubmit : handleSaveChanges;
        const submitting = isCreate ? creating : updatingQty;
        const submitLabel = isCreate ? "Create" : "Save";

        return (
            <div className="convert-overlay" onClick={onClose}>
                <div className="view-modal" onClick={(e) => e.stopPropagation()} style={{ fontSize: "13px" }}>

                    {/* ── Title Bar ── */}
                    <div style={{ padding: "10px 20px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={{ flex: 1, fontWeight: 700, fontSize: "1.05rem", color: "#1e293b", whiteSpace: "nowrap" }}>
                            {isCreate ? "Create Planned Order" : `Planned Order : ${viewModalData.plannedOrderNo || ""}`}
                        </div>
                        <button onClick={onClose}
                            style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#64748b" }}>×</button>
                    </div>

                    {/* ── Scrollable Body ── */}
                    <div className="hide-scrollbar" style={{ flex: 1, overflowY: "auto", padding: "16px 20px", scrollbarWidth: "none", msOverflowStyle: "none" }}>

                        {/* Product Type */}
                        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                            <label style={{ ...mLabel, marginBottom: 0, whiteSpace: "nowrap", minWidth: 100 }}>Product Type</label>
                            <div style={{ width: 220, flexShrink: 0 }}>
                                <input style={mInput} readOnly value={(isCreate ? createForm.productType : viewModalData.productType) || ""} />
                            </div>
                        </div>

                        {/* Sales Order — read-only pegged-lines table in view mode;
                            editable Sales Order/Item picker (optional peg) in create mode,
                            same fields Production Order's create modal captures. */}
                        {isCreate ? (
                            <div style={{ marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={{ ...soThStyle, width: "18%" }}>Sales Order</th>
                                            <th style={{ ...soThStyle, width: "12%" }}>Item</th>
                                            <th style={{ ...soThStyle, width: "18%" }}>Product Code</th>
                                            <th style={{ ...soThStyle, width: "20%" }}>Description</th>
                                            <th style={{ ...soThStyle, width: "12%" }}>Req. Date</th>
                                            <th style={{ ...soThStyle, textAlign: "right", width: "10%" }}>Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr style={{ background: "#fff" }}>
                                            <td style={{ ...soTdStyle, padding: 6 }}>
                                                <Select
                                                    options={createSalesOrderOptions.map(s => ({ value: s.so_id, label: s.so_id }))}
                                                    value={createForm.salesOrder}
                                                    onChange={handleCreateSalesOrderChange}
                                                    isClearable placeholder="Select SO"
                                                    menuPortalTarget={document.body} menuPosition="fixed"
                                                    styles={{ ...smallSelectStyles, control: (base, state) => ({ ...smallSelectStyles.control(base, state), minHeight: 28, height: 28 }) }}
                                                />
                                            </td>
                                            <td style={{ ...soTdStyle, padding: 6 }}>
                                                <Select
                                                    options={createSoItemOptions.map((it, i) => ({ value: it.id, label: `${i + 1}` }))}
                                                    value={createForm.item}
                                                    onChange={handleCreateSoItemChange}
                                                    isClearable placeholder="Item"
                                                    isDisabled={!createForm.salesOrder || createSoItemOptions.length === 0}
                                                    menuPortalTarget={document.body} menuPosition="fixed"
                                                    styles={{ ...smallSelectStyles, control: (base, state) => ({ ...smallSelectStyles.control(base, state), minHeight: 28, height: 28 }) }}
                                                />
                                            </td>
                                            <td style={soTdStyle}>{createForm.productCode?.value || createForm.productCode || ""}</td>
                                            <td style={{ ...soTdStyle, wordBreak: "break-word" }}>{createForm.description || ""}</td>
                                            <td style={soTdStyle}>{formatDate(createForm.requirementDate) || ""}</td>
                                            <td style={{ ...soTdStyle, textAlign: "right" }}>{createForm.soQty || ""}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div style={{ marginBottom: 16, border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
                                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                    <thead>
                                        <tr>
                                            <th style={soThStyle}>Sales Order</th>
                                            <th style={soThStyle}>Item</th>
                                            <th style={soThStyle}>Product Code</th>
                                            <th style={soThStyle}>Description</th>
                                            <th style={soThStyle}>Req. Date</th>
                                            <th style={{ ...soThStyle, textAlign: "right" }}>Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {salesOrderRows.map((row, idx) => (
                                            <tr key={row.soItemId || idx} style={{ background: idx % 2 ? "#fafbfc" : "#fff" }}>
                                                <td style={soTdStyle}>
                                                    {!isFieldVisible('sales_order') ? <MaskedValue height={22} /> : (
                                                        (!isCreate && row.soNumber) ? (
                                                            <a
                                                                href="/cable/sales/sales-order"
                                                                title={`Open Sales Order ${row.soNumber}`}
                                                                onClick={(e) => { e.preventDefault(); handleOpenSalesOrder(row.soNumber, viewModalData.plannedOrderNo); }}
                                                                style={{ color: "#2563eb", textDecoration: "underline", cursor: "pointer" }}
                                                            >
                                                                {row.soNumber}
                                                            </a>
                                                        ) : (row.soNumber || "")
                                                    )}
                                                </td>
                                                <td style={soTdStyle}>
                                                    {!isFieldVisible('so_item') ? <MaskedValue height={22} /> : (row.soItemSeq || "")}
                                                </td>
                                                <td style={soTdStyle}>{row.productCode || ""}</td>
                                                <td style={{ ...soTdStyle, wordBreak: "break-word" }}>{row.productDescription || ""}</td>
                                                <td style={soTdStyle}>{row.reqDate || ""}</td>
                                                <td style={{ ...soTdStyle, textAlign: "right" }}>
                                                    {!isFieldVisible('so_qty') ? <MaskedValue height={22} /> : (
                                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6 }}>
                                                        <span>{row.soQty || ""}</span>
                                                        <span
                                                            onClick={() => { setActiveCharType("soQty"); setActiveSoCharRow(row); setShowClassModal(true); }}
                                                            title="View SO Qty Characteristics"
                                                            style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}
                                                        >▶</span>
                                                    </div>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Construction | Code | Cable Type | Category | Cores | Color | Cross Section | Std. Ref | Voltage */}
                        {/* <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", marginBottom: 10 }}>
                            <div style={{ display: "flex", alignItems: "flex-end", gap: 8, flexWrap: "wrap" }}>
                                {specFields.map(({ label, value }) => (
                                    <div key={label} style={{ minWidth: 110, flex: "1 1 100px", maxWidth: 160 }}>
                                        <label style={{ fontSize: 10, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 3, whiteSpace: "nowrap" }}>{label}</label>
                                        <input style={{ ...mInput, height: 28, fontSize: 11 }} readOnly value={value || ""} />
                                    </div>
                                ))}
                            </div>
                        </div> */}

                        {/* Product Code | Description | UOM | Order Qty ▶ | Alt. BOM | Base Unit */}
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "10px 14px", marginBottom: 10 }}>
                            <div>
                                <label style={mLabel}>Product Code{isCreate ? " *" : ""}</label>
                                {isCreate ? (
                                    <Select
                                        options={productCodeMasterOptions}
                                        value={createForm.productCode}
                                        onChange={handleCreateProductCodeChange}
                                        isClearable placeholder="Select Product Code"
                                        menuPortalTarget={document.body} menuPosition="fixed"
                                        styles={{ ...smallSelectStyles, control: (base, state) => ({ ...smallSelectStyles.control(base, state), minHeight: 30, height: 30 }) }}
                                    />
                                ) : !isFieldVisible('product_code') ? (
                                    <MaskedValue />
                                ) : (
                                    <input style={mInput} readOnly value={viewModalData.productCode || ""} />
                                )}
                            </div>
                            <div style={{ gridColumn: "span 2" }}>
                                <label style={mLabel}>Description</label>
                                {!isCreate && !isFieldVisible('description') ? (
                                    <MaskedValue />
                                ) : (
                                    <input style={mInput} readOnly
                                        value={(isCreate ? createForm.description : viewModalData.description) || ""}
                                        title={(isCreate ? createForm.description : viewModalData.description) || ""} />
                                )}
                            </div>
                            <div>
                                <label style={mLabel}>UOM</label>
                                {!isCreate && !isFieldVisible('uom') ? (
                                    <MaskedValue />
                                ) : (
                                    <input style={mInput} readOnly value={(isCreate ? createForm.uom : viewModalData.uom) || ""} />
                                )}
                            </div>
                            <div>
                                <label style={mLabel}>Order Qty{isCreate ? " *" : ""}</label>
                                {!isCreate && !isFieldVisible('qty') ? (
                                    <MaskedValue />
                                ) : (
                                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                                    <input
                                        style={{ ...mInput, flex: 1, background: qtyReadOnly ? "#f1f5f9" : "#fff" }}
                                        type="number" min="0" step="any"
                                        readOnly={qtyReadOnly}
                                        disabled={!isCreate && updatingQty}
                                        value={qtyValue}
                                        onChange={onQtyChange}
                                        title={
                                            !isCreate && viewModalData.isConverted
                                                ? "Already converted to a Production Order — qty cannot be edited"
                                                : !isCreate && (!canEdit || isQtyFieldLocked)
                                                    ? "Your role doesn't have edit access to Order Qty"
                                                    : isFinishedGood
                                                        ? "Finished Goods qty is set via Order Qty Characteristics (▶)"
                                                        : "Order Qty"
                                        }
                                    />
                                    {isCreate ? (
                                        createForm.orderQtyChars.length > 0 && (
                                            <span
                                                onClick={() => setShowCreateClassModal(true)}
                                                title="Edit Order Qty Characteristics"
                                                style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}
                                            >▶</span>
                                        )
                                    ) : (
                                        (viewModalData.orderQtyChars || []).length > 0 && (
                                            <span
                                                onClick={() => { setActiveCharType("orderQty"); setShowClassModal(true); }}
                                                title={canEditQtyChars ? "Edit Order Qty Characteristics" : "View Order Qty Characteristics"}
                                                style={{ cursor: "pointer", color: "#151616", fontSize: 14, userSelect: "none", flexShrink: 0 }}
                                            >▶</span>
                                        )
                                    )}
                                </div>
                                )}
                            </div>
                            <div>
                                <label style={mLabel}>Alt. BOM{isCreate ? " *" : ""}</label>
                                {isCreate ? (
                                    <Select
                                        options={createAltBomOptions}
                                        value={createForm.altBom}
                                        onChange={handleCreateAltBomChange}
                                        isClearable placeholder="Select Alt. BOM"
                                        isDisabled={!createForm.productCode}
                                        menuPortalTarget={document.body} menuPosition="fixed"
                                        styles={{ ...smallSelectStyles, control: (base, state) => ({ ...smallSelectStyles.control(base, state), minHeight: 30, height: 30 }) }}
                                    />
                                ) : !isFieldVisible('alt_bom') ? (
                                    <MaskedValue />
                                ) : (
                                    <input style={mInput} readOnly value={viewModalData.altBom || ""} placeholder="—" />
                                )}
                            </div>
                            <div>
                                <label style={mLabel}>Base Unit</label>
                                {!isCreate && !isFieldVisible('base_unit') ? (
                                    <MaskedValue />
                                ) : (
                                    <input style={mInput} readOnly value={(isCreate ? createForm.baseUnit : viewModalData.baseUnit) || ""} placeholder="—" />
                                )}
                            </div>
                        </div>

                        {/* Dates row — create: Prod Start/End (editable) + Created By (auto) +
                            Requirement Date (editable), same fields Production Order's create
                            modal shows. view: prod start/end (read-only) + created by. */}
                        {isCreate ? (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 14px", marginBottom: 10 }}>
                                <div>
                                    <label style={mLabel}>Prod. Start Date</label>
                                    <div title="Set this from the Machine Time tab's first row Start Date">
                                        <DateField value={createForm.startDate} onChange={() => {}} disabled />
                                    </div>
                                </div>
                                <div>
                                    <label style={mLabel}>Prod. Start Time</label>
                                    <div title="Set this from the Machine Time tab's first row Start Time">
                                        <TimeField value={createForm.startTime} onChange={() => {}} disabled />
                                    </div>
                                </div>
                                <div>
                                    <label style={mLabel}>Prod. End Date</label>
                                    <div title="Calculated automatically from Machine Time">
                                        <DateField value={createForm.endDate} onChange={() => {}} disabled />
                                    </div>
                                </div>
                                <div>
                                    <label style={mLabel}>Prod. End Time</label>
                                    <div title="Calculated automatically from Machine Time">
                                        <TimeField value={createForm.endTime} onChange={() => {}} disabled />
                                    </div>
                                </div>
                                <div>
                                    <label style={mLabel}>Created By</label>
                                    <input style={{ ...mInput, background: "#f1f5f9" }} readOnly value={getCurrentUserId() ?? ""} />
                                </div>
                                <div>
                                    <label style={mLabel}>Requirement Date</label>
                                    <DateField
                                        value={createForm.requirementDate}
                                        onChange={(v) => setCreateForm(prev => ({ ...prev, requirementDate: v }))}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(6, 1fr)", gap: "10px 14px", marginBottom: 10 }}>
                                <div>
                                    <label style={mLabel}>Prod. Start Date</label>
                                    {!isFieldVisible('start_date') ? <MaskedValue /> : (
                                        <input style={mInput} readOnly value={formatDate(viewModalData.startDate) || ""} />
                                    )}
                                </div>
                                <div>
                                    <label style={mLabel}>Prod. Start Time</label>
                                    {!isFieldVisible('start_date') ? <MaskedValue /> : (
                                        <input style={mInput} readOnly value={viewModalData.startTime || ""} />
                                    )}
                                </div>
                                <div>
                                    <label style={mLabel}>Prod. End Date</label>
                                    {!isFieldVisible('end_date') ? <MaskedValue /> : (
                                        <input style={mInput} readOnly value={formatDate(viewModalData.endDate) || ""} />
                                    )}
                                </div>
                                <div>
                                    <label style={mLabel}>Prod. End Time</label>
                                    {!isFieldVisible('end_date') ? <MaskedValue /> : (
                                        <input style={mInput} readOnly value={viewModalData.endTime || ""} />
                                    )}
                                </div>
                                <div>
                                    <label style={mLabel}>Created By</label>
                                    {!isFieldVisible('created_by') ? <MaskedValue /> : (
                                        <input style={mInput} readOnly value={viewModalData.createdBy || ""} />
                                    )}
                                </div>
                                <div>
                                    <label style={mLabel}>Creation Date</label>
                                    {!isFieldVisible('created_by') ? <MaskedValue /> : (
                                        <input style={mInput} readOnly value={formatDate(viewModalData.createdOn) || ""} />
                                    )}
                                </div>
                            </div>
                        )}

                        <div style={{ borderTop: "1px solid #e2e8f0", marginBottom: 10 }} />

                        {isCreate && createLoadingBom && (
                            <div style={{ textAlign: "center", padding: "10px 0", color: "#64748b", fontSize: 12 }}>
                                Loading BOM &amp; routing…
                            </div>
                        )}

                        {isCreate && (
                            <CharacteristicsModal
                                modalVisible={showCreateClassModal}
                                selectedCharacteristics={createForm.orderQtyChars || []}
                                isEditing={true}
                                handleChange={(idx, val) => {
                                    setCreateForm(prev => {
                                        const updated = [...(prev.orderQtyChars || [])];
                                        updated[idx] = { ...updated[idx], value: val };
                                        const computedQty = computeOrderQtyFromChars(updated);
                                        return {
                                            ...prev,
                                            orderQtyChars: updated,
                                            // Auto-fill Order Qty once both Drum/Coil
                                            // characteristics have valid values; otherwise
                                            // leave whatever Order Qty is already there.
                                            ...(computedQty !== null ? { quantity: String(computedQty) } : {}),
                                        };
                                    });
                                }}
                                handleClose={() => setShowCreateClassModal(false)}
                                // Over/Under Tolerances checkboxes are linked — checking
                                // either one checks both, same as Sales Order / Production
                                // Order's own handleToggleTolerance.
                                handleToggleTolerance={(idx) => {
                                    const list = createForm.orderQtyChars || [];
                                    const target = list[idx];
                                    const name = (target?.characteristic || '').trim().toLowerCase();
                                    if (name !== 'over tolerance' && name !== 'under tolerance') return;

                                    const newChecked = !target.tolerance_applied;
                                    const updated = list.map((c) => {
                                        const cName = (c.characteristic || '').trim().toLowerCase();
                                        if (cName === 'over tolerance' || cName === 'under tolerance') {
                                            return { ...c, tolerance_applied: newChecked };
                                        }
                                        return c;
                                    });
                                    setCreateForm(prev => ({ ...prev, orderQtyChars: updated }));
                                }}
                            />
                        )}

                        {!isCreate && (
                            <CharacteristicsModal
                                modalVisible={showClassModal}
                                selectedCharacteristics={
                                    activeCharType === "soQty"
                                        ? (activeSoCharRow?.soQtyChars || viewModalData.soQtyChars || [])
                                        : (viewModalData.orderQtyChars || [])
                                }
                                // SO Qty characteristics stay view-only (they belong to the
                                // Sales Order line, edited from there). Order Qty
                                // characteristics become editable here only for FG products
                                // with edit access — same as Production Order's qty-via-
                                // characteristics entry — so the header Order Qty is
                                // (re)computed from Drum/Coil values entered in this modal
                                // instead of being typed directly.
                                isEditing={activeCharType === "orderQty" && canEditQtyChars}
                                handleChange={(idx, val) => {
                                    if (activeCharType !== "orderQty" || !canEditQtyChars) return;
                                    setViewModalData(prev => {
                                        const updated = [...(prev.orderQtyChars || [])];
                                        updated[idx] = { ...updated[idx], value: val };
                                        const computedQty = computeOrderQtyFromChars(updated);
                                        return {
                                            ...prev,
                                            orderQtyChars: updated,
                                            // Auto-fill Order Qty once both Drum/Coil
                                            // characteristics have valid values; otherwise
                                            // leave whatever Order Qty is already there.
                                            ...(computedQty !== null ? { qty: String(computedQty) } : {}),
                                        };
                                    });
                                }}
                                handleToggleTolerance={(idx) => {
                                    if (activeCharType !== "orderQty" || !canEditQtyChars) return;
                                    const list = viewModalData.orderQtyChars || [];
                                    const target = list[idx];
                                    const name = (target?.characteristic || '').trim().toLowerCase();
                                    if (name !== 'over tolerance' && name !== 'under tolerance') return;

                                    const newChecked = !target.tolerance_applied;
                                    const updated = list.map((c) => {
                                        const cName = (c.characteristic || '').trim().toLowerCase();
                                        if (cName === 'over tolerance' || cName === 'under tolerance') {
                                            return { ...c, tolerance_applied: newChecked };
                                        }
                                        return c;
                                    });
                                    setViewModalData(prev => ({ ...prev, orderQtyChars: updated }));
                                }}
                                handleClose={() => { setShowClassModal(false); setActiveSoCharRow(null); }}
                            />
                        )}

                        {/* ══ Accordion Sections — identical in both modes ══ */}
                        {[
                            { key: "material_details", label: "Material Details" },
                            { key: "machine_time", label: "Machine Time" },
                            { key: "special_remarks", label: "Special Remarks" },
                        ].map(({ key, label }) => (
                            <div key={key} style={{ marginBottom: 6, border: "1px solid #e2e8f0", borderRadius: 6, overflow: "hidden" }}>
                                <div
                                    onClick={() => toggleSection(key)}
                                    style={{
                                        display: "flex", justifyContent: "space-between", alignItems: "center",
                                        padding: "8px 14px", cursor: "pointer",
                                        background: expandedSections[key] ? "#f1f5f9" : "#f8fafc",
                                        borderBottom: expandedSections[key] ? "1px solid #e2e8f0" : "none",
                                        userSelect: "none",
                                    }}
                                >
                                    <span style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{label}</span>
                                    <span style={{
                                        fontSize: 12, color: "#64748b", transition: "transform 0.2s", display: "inline-block",
                                        transform: expandedSections[key] ? "rotate(180deg)" : "rotate(0deg)",
                                    }}>▼</span>
                                </div>

                                {expandedSections[key] && (
                                    <div style={{ padding: "12px 14px" }}>
                                        {key === "material_details" && (
                                            <ComponentsTable rows={componentsRows} isCreate={isCreate} />
                                        )}
                                        {key === "machine_time" && (
                                            OperationsTable({
                                                rows: operationsRows, qty: operationsQty, baseUnit: operationsBaseUnit,
                                                editable: isCreate,
                                                onRowChange: isCreate ? handleCreateOpChange : undefined,
                                            })
                                        )}
                                        {key === "special_remarks" && (
                                            <div>
                                                <textarea
                                                    rows={4}
                                                    value={remarksValue}
                                                    onChange={onRemarksChange}
                                                    readOnly={remarksReadOnly}
                                                    disabled={!isCreate && updatingQty}
                                                    placeholder={remarksReadOnly ? "No special remarks" : "Optional — enter any special remarks for this order"}
                                                    title={
                                                        !isCreate && viewModalData.isConverted
                                                            ? "Already converted to a Production Order — remarks cannot be edited"
                                                            : !isCreate && !canEdit
                                                                ? "Your role doesn't have edit access"
                                                                : "Special Remarks"
                                                    }
                                                    style={{
                                                        width: "100%", fontSize: 12, padding: "8px 10px",
                                                        border: "1px solid #dee2e6", borderRadius: 4,
                                                        background: remarksReadOnly ? "#f1f5f9" : "#fff",
                                                        color: "#1e293b", boxSizing: "border-box",
                                                        resize: "vertical", outline: "none", fontFamily: "inherit",
                                                    }}
                                                />
                                                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 4 }}>
                                                    {remarksReadOnly
                                                        ? "Carried over to the Production Order on conversion."
                                                        : "Optional. Carried over to the Production Order when this order is converted."}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* ── Footer ── */}
                    <div style={{ padding: "15px 20px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 10 }}>
                        <button
                            onClick={onClose}
                            disabled={submitting}
                            style={{ background: "#e2e8f0", border: "none", padding: "6px 18px", borderRadius: 6, cursor: "pointer", fontSize: 13, fontWeight: 500 }}
                        >
                            {isCreate ? "Cancel" : "Close"}
                        </button>
                        {showSubmitButton && (
                            <button
                                onClick={onSubmit}
                                disabled={submitting}
                                style={{
                                    background: submitting ? "#94a3b8" : "#16a34a", color: "#fff", border: "none",
                                    padding: "6px 18px", borderRadius: 6, cursor: submitting ? "default" : "pointer",
                                    fontSize: 13, fontWeight: 600,
                                }}
                            >
                                {submitting ? `${submitLabel}…` : submitLabel}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    // ══════════════════════════════════════════════════════════
    // CREATE PLANNED ORDER (manual) — logic
    // Reuses the SAME product-level lookups Production Order's create
    // modal uses (product-info / alt-boms / components-by-product-bom /
    // routing-data) since BOM + routing data isn't order-type-specific.
    // ══════════════════════════════════════════════════════════
    const resetCreateForm = () => {
        setCreateForm({
            productCode: null, description: "", uom: "", productType: "",
            quantity: "", altBom: null, baseUnit: "", requirementDate: "",
            construction: "", code: "", cableType: "", productCategory: "",
            noOfCores: "", color: "", nominalCrossSection: "",
            referenceOfStandard: "", voltage: "",
            salesOrder: null, item: null, customer: "", purchaseOrder: "", soQty: "",
            startDate: "", startTime: "", endDate: "", endTime: "",
            orderQtyChars: [],
            specialRemarks: "",
        });
        setCreateAltBomOptions([]);
        setCreateComponents([]);
        setCreateOperations([]);
        setCreateSoItemOptions([]);
        setCreateCharacteristicsConfig([]);
        setCreateActiveTab("material_details");
    };

    const openCreateModal = () => {
        resetCreateForm();
        setShowCreateModal(true);
    };

    const closeCreateModal = () => {
        if (creating) return;
        setShowCreateModal(false);
        resetCreateForm();
    };

    // Product Code picked -> product-info + alt-bom options + spec strip auto-fill
    // + characteristics config (mirrors Production Order's create modal exactly).
    const handleCreateProductCodeChange = async (opt) => {
        setCreateForm(prev => ({
            ...prev, productCode: opt, description: "", uom: "", productType: "",
            altBom: null, baseUnit: "",
            construction: "", code: "", cableType: "", productCategory: "",
            noOfCores: "", color: "", nominalCrossSection: "",
            referenceOfStandard: "", voltage: "",
            orderQtyChars: [],
        }));
        setCreateAltBomOptions([]);
        setCreateComponents([]);
        setCreateOperations([]);
        setCreateCharacteristicsConfig([]);
        if (!opt) return;

        // Spec strip (Construction / Code / Cable Type / Category / Cores / Color /
        // Cross Section / Std. Ref / Voltage) comes from the same product master list
        // already fetched into allProductCodesData — not from product-info — exactly
        // how Production Order's create modal resolves it (see `matchedProduct`).
        const matchedProduct = allProductCodesData.find(p => p.productCode === opt.value);
        if (matchedProduct) {
            setCreateForm(prev => ({
                ...prev,
                construction: matchedProduct.construction || "",
                code: matchedProduct.code || "",
                cableType: matchedProduct.cableType || "",
                productCategory: matchedProduct.category || "",
                noOfCores: matchedProduct.noOfCores || "",
                color: matchedProduct.color || "",
                nominalCrossSection: matchedProduct.nominalCrossSection || "",
                referenceOfStandard: matchedProduct.referenceOfStandard || "",
                voltage: matchedProduct.voltage || "",
            }));
        }

        try {
            const [infoRes, altBomRes, charRes] = await Promise.all([
                apiFetch(`${PURCHASE_API}/production-order/create/product-info?productCode=${encodeURIComponent(opt.value)}`),
                apiFetch(`${PURCHASE_API}/production-order/create/alt-boms?productCode=${encodeURIComponent(opt.value)}`),
                apiFetch(`${PURCHASE_API}/production-order/create/characteristics?productCode=${encodeURIComponent(opt.value)}`),
            ]);
            const info = infoRes.ok ? await infoRes.json() : {};
            const altBoms = altBomRes.ok ? await altBomRes.json() : [];
            const chars = charRes.ok ? await charRes.json() : [];

            setCreateForm(prev => ({
                ...prev,
                description: info.productDescription || info.description || "",
                uom: info.uom || "",
                productType: info.productType || "",
                baseUnit: info.base_unit || "",
                // Fall back to product-info's own fields if the master-list match missed
                // any of these (keeps the strip filled even for edge-case product codes).
                construction: prev.construction || info.construction || "",
                cableType: prev.cableType || info.cableType || "",
                productCategory: prev.productCategory || info.productCategory || "",
            }));
            setCreateAltBomOptions(
                (Array.isArray(altBoms) ? altBoms : []).map(a => ({
                    value: a.alternative_bom, label: a.alternative_bom,
                }))
            );

            const chartList = Array.isArray(chars) ? chars : [];
            setCreateCharacteristicsConfig(chartList);
            setCreateForm(prev => ({
                ...prev,
                orderQtyChars: chartList.map(c => ({ characteristic: c.characteristic, value: "", calculation_type: c.calculation_type })),
            }));
        } catch (e) {
            console.error("Failed to load product info for Create Planned Order", e);
        }
    };

    // ── Sales Order picked (Create Planned Order — optional peg, same fields
    // Production Order's create modal shows: Item / Customer / PO / Req Date / Qty) ──
    // Normalize a characteristic name for matching between the SO's own
    // characteristics list and the product's authoritative characteristics
    // config (case/whitespace can differ between the two sources).
    const normCharFE = (s) => (s || "").toString().trim().toLowerCase();

    const handleCreateSalesOrderChange = async (opt) => {
        if (!opt) {
            setCreateForm(prev => ({
                ...prev, salesOrder: null, item: null, customer: "", purchaseOrder: "", soQty: "",
            }));
            setCreateSoItemOptions([]);
            return;
        }
        setCreateForm(prev => ({ ...prev, salesOrder: opt, item: null, customer: "", purchaseOrder: "", soQty: "" }));

        let items = [];
        try {
            const itemsRes = await apiFetch(`${PURCHASE_API}/production-order/sales-order-items?soId=${encodeURIComponent(opt.value)}`);
            items = itemsRes.ok ? await itemsRes.json() : [];
        } catch (err) {
            console.error("Failed to fetch SO items", err);
        }
        setCreateSoItemOptions(Array.isArray(items) ? items : []);

        const autoFill = items.length === 1;
        try {
            const res = await apiFetch(`${PURCHASE_API}/production-order/sales-order-details?soId=${encodeURIComponent(opt.value)}`);
            const d = res.ok ? await res.json() : null;
            if (d) {
                const customerName = d.customer || d.customer_name || "";
                setCreateForm(prev => ({
                    ...prev,
                    customer: customerName,
                    purchaseOrder: d.purchase_order || "",
                    ...(autoFill ? {
                        item: items[0] ? { value: items[0].id, label: `${items[0].id}` } : null,
                        soQty: d.quantity || "",
                        quantity: d.quantity || prev.quantity,
                        requirementDate: d.requirement_date ? String(d.requirement_date).substring(0, 10) : prev.requirementDate,
                    } : {}),
                }));

                // Sales Order line always maps to one specific product — pull it
                // in (backend returns it as `product_code`, snake_case) and reuse
                // the existing Product Code handler so description, UOM, spec
                // strip, Alt. BOM list and Characteristics CONFIG all populate
                // exactly like a manual Product Code pick would.
                if (autoFill && d.product_code) {
                    await autoFillProductCode(d.product_code);
                    // Now overlay the actual VALUES this SO line has for those
                    // characteristics (handleCreateProductCodeChange only sets up
                    // the empty config — the real values live on the SO row).
                    await applySoCharacteristics(d.product_code, d.characteristics);
                }
            }
        } catch (err) {
            console.error("Failed to fetch SO details", err);
        }
    };

    // Shared helper: given a productCode string (from an SO/Item lookup),
    // find it in productCodeMasterOptions and run it through the same
    // handler a manual Product Code selection would use.
    const autoFillProductCode = async (productCode) => {
        const matchedOpt = productCodeMasterOptions.find(p => p.value === productCode);
        if (matchedOpt) {
            await handleCreateProductCodeChange(matchedOpt);
        } else {
            // Fall back to a synthetic option so the Select box still shows the
            // code even if it wasn't present in the pre-fetched master list.
            await handleCreateProductCodeChange({ value: productCode, label: productCode });
        }
    };

    // Merge the SO line's own characteristic VALUES onto the product's
    // authoritative characteristics CONFIG (characteristic + calculation_type),
    // then push the merged list into createForm.orderQtyChars. Re-fetches the
    // config directly instead of trusting createCharacteristicsConfig state,
    // since that state update from handleCreateProductCodeChange (above) may
    // not have flushed yet when this runs.
    const applySoCharacteristics = async (productCode, soCharacteristics) => {
        if (!productCode) return;
        let configChars = [];
        try {
            const charRes = await apiFetch(`${PURCHASE_API}/production-order/create/characteristics?productCode=${encodeURIComponent(productCode)}`);
            configChars = charRes.ok ? await charRes.json() : [];
        } catch (e) {
            console.error("Failed to fetch product-class characteristics config", e);
        }
        if (!Array.isArray(configChars) || configChars.length === 0) return;

        const soValueMap = new Map(
            (Array.isArray(soCharacteristics) ? soCharacteristics : [])
                .map(c => [normCharFE(c.characteristic), c.value])
        );

        const merged = configChars.map(cfg => ({
            characteristic: cfg.characteristic,
            calculation_type: cfg.calculation_type,
            value: soValueMap.get(normCharFE(cfg.characteristic)) ?? "",
        }));

        setCreateCharacteristicsConfig(configChars);
        setCreateForm(prev => ({ ...prev, orderQtyChars: merged }));
    };

    const handleCreateSoItemChange = async (opt) => {
        if (!opt) { setCreateForm(prev => ({ ...prev, item: null, soQty: "" })); return; }
        setCreateForm(prev => ({ ...prev, item: opt }));
        if (!createForm.salesOrder) return;
        try {
            const res = await apiFetch(`${PURCHASE_API}/production-order/sales-order-details?soId=${encodeURIComponent(createForm.salesOrder.value)}&itemId=${encodeURIComponent(opt.value)}`);
            const d = res.ok ? await res.json() : null;
            if (d) {
                setCreateForm(prev => ({
                    ...prev,
                    soQty: d.quantity || "",
                    quantity: d.quantity || prev.quantity,
                    requirementDate: d.requirement_date ? String(d.requirement_date).substring(0, 10) : prev.requirementDate,
                }));

                // Same auto-fill as above, but for the case where the SO has
                // multiple items and the user had to pick one explicitly.
                if (d.product_code) {
                    await autoFillProductCode(d.product_code);
                    await applySoCharacteristics(d.product_code, d.characteristics);
                }
            }
        } catch (err) {
            console.error("Failed to fetch SO item details", err);
        }
    };


    // Alt BOM picked -> BOM-exploded components + routing/machine-time,
    // auto-populated exactly the way Production Order's create modal does.
    const handleCreateAltBomChange = async (opt) => {
        setCreateForm(prev => ({ ...prev, altBom: opt }));
        setCreateComponents([]);
        setCreateOperations([]);
        if (!opt || !createForm.productCode) return;

        setCreateLoadingBom(true);
        try {
            const productCode = createForm.productCode.value;
            const [compRes, routingRes] = await Promise.all([
                apiFetch(`${PURCHASE_API}/production-order/routings/components/by-product-bom?productCode=${encodeURIComponent(productCode)}&alternative_bom=${encodeURIComponent(opt.value)}`),
                apiFetch(`${PURCHASE_API}/production-order/create/routing-data?productCode=${encodeURIComponent(productCode)}&alternative_bom=${encodeURIComponent(opt.value)}`),
            ]);
            const comps = compRes.ok ? await compRes.json() : [];
            const routing = routingRes.ok ? await routingRes.json() : {};

            setCreateComponents(Array.isArray(comps) ? comps : []);
            setCreateOperations(Array.isArray(routing.machineTime) ? routing.machineTime : []);
            if (routing.base_unit) {
                setCreateForm(prev => ({ ...prev, baseUnit: routing.base_unit }));
            }
        } catch (e) {
            console.error("Failed to load BOM/routing for Create Planned Order", e);
        } finally {
            setCreateLoadingBom(false);
        }
    };

    // Order Qty for a Finished Good sold by Drum/Coil is calculated, not
    // typed directly: Order Qty = No. of Drum/Coil × Length of the
    // Drum/Coil (the two characteristics editable from the Order Qty ▶
    // characteristics popup). Runs after every keystroke in that modal so
    // the Order Qty field on the header always reflects the latest values.
    // Falls through (returns null) until both characteristics have a
    // valid positive number, so a manually-typed Order Qty isn't clobbered
    // for products that don't carry these two characteristics at all.
    const normCharName = (s) => (s || "").trim().toLowerCase();
    const computeOrderQtyFromChars = (chars) => {
        const drumChar   = (chars || []).find(c => normCharName(c.characteristic) === "no. of drum/coil");
        const lengthChar = (chars || []).find(c => normCharName(c.characteristic) === "length of the drum/coil");
        if (!drumChar || !lengthChar) return null;

        const drums  = parseFloat(drumChar.value);
        const length = parseFloat(lengthChar.value);
        if (!isFinite(drums) || !isFinite(length) || drums <= 0 || length <= 0) return null;

        return drums * length;
    };

    const createOrderQty = Number(createForm.quantity) || 0;
    const createBaseUnit = Number(createForm.baseUnit) || 1;

    // Same total-qty math as the view modal: (order_qty * bom_qty) / base_unit
    const createComponentsWithTotals = createComponents.map((c, i) => ({
        ...c,
        item: c.item || i + 1,
        total_qty: createOrderQty && c.quantity ? (createOrderQty * Number(c.quantity)) / createBaseUnit : 0,
    }));

    // createOperations rows come from routing.machineTime (setup/machine
    // values) -- OperationsTable (view mode's table) expects row.setup and
    // row.run_time, so map machine -> run_time for display only.
    const createOperationsForDisplay = createOperations.map(op => ({
        ...op, run_time: op.machine,
    }));

    // ── Machine Time scheduling (Create mode only) ──
    // Setup is a fixed number of minutes; Run Time (stored as `machine` on the
    // raw row) scales with qty/baseUnit exactly like OperationsTable's own
    // calcRunTime. Both are treated as minutes (matches the "Min" UOM shown
    // in the table).
    const calcPlannedOpEndDateTime = (startDate, startTime, setupRaw, runTimeRaw, qty, baseUnit) => {
        if (!startDate || !startTime) return { end_date: "", end_time: "" };
        const bUnit = Number(baseUnit) || 1;
        const prodQty = Number(qty) || 1;
        const setupMin = Number(setupRaw || 0);
        const runMin = (Number(runTimeRaw || 0) * prodQty) / bUnit;
        const totalMinutes = setupMin + runMin;

        const start = new Date(`${startDate}T${startTime}`);
        if (isNaN(start.getTime())) return { end_date: startDate, end_time: startTime };
        const end = new Date(start.getTime() + totalMinutes * 60 * 1000);
        return {
            end_date: `${end.getFullYear()}-${String(end.getMonth() + 1).padStart(2, '0')}-${String(end.getDate()).padStart(2, '0')}`,
            end_time: `${String(end.getHours()).padStart(2, '0')}:${String(end.getMinutes()).padStart(2, '0')}`,
        };
    };

    // Editing a Machine Time row's Start Date/Time cascades forward through
    // every later row (each row's end becomes the next row's start), then
    // syncs the header Prod. Start/End Date & Time from the first/last row —
    // same pattern as Production Order's handleOpChange.
    const handleCreateOpChange = (idx, field, value) => {
        setCreateOperations(prevRows => {
            const newRows = [...prevRows];
            newRows[idx] = { ...newRows[idx], [field]: value };

            for (let i = idx; i < newRows.length; i++) {
                const scheduling = calcPlannedOpEndDateTime(
                    newRows[i].start_date,
                    newRows[i].start_time || "00:00",
                    newRows[i].setup,
                    newRows[i].machine,
                    createOrderQty,
                    createBaseUnit
                );
                newRows[i].end_date = scheduling.end_date;
                newRows[i].end_time = scheduling.end_time;

                if (newRows[i + 1]) {
                    newRows[i + 1] = {
                        ...newRows[i + 1],
                        start_date: scheduling.end_date,
                        start_time: scheduling.end_time,
                    };
                }
            }

            const firstRow = newRows[0];
            const lastRow = newRows[newRows.length - 1];
            setCreateForm(prev => ({
                ...prev,
                startDate: firstRow?.start_date || prev.startDate,
                startTime: firstRow?.start_time || prev.startTime,
                endDate: lastRow?.end_date || prev.endDate,
                endTime: lastRow?.end_time || prev.endTime,
            }));

            return newRows;
        });
    };

    const handleCreateSubmit = async () => {
        if (!createForm.productCode) { toast.warning("Please select a Product Code."); return; }
        if (!createOrderQty || createOrderQty <= 0) { toast.warning("Please enter a valid Order Qty."); return; }
        if (!createForm.altBom) { toast.warning("Please select an Alternative BOM."); return; }

        setCreating(true);
        try {
            const payload = {
                productCode: createForm.productCode.value,
                alternative_bom: createForm.altBom.value,
                header_uom: createForm.uom,
                base_unit: createForm.baseUnit,
                quantity: createOrderQty,
                requirement_date: createForm.requirementDate || null,
                // ── optional free-text remarks ──
                special_remarks: (createForm.specialRemarks || "").trim() || null,
                // ── optional Sales Order peg ──
                salesOrderId: createForm.salesOrder ? createForm.salesOrder.value : "",
                soItemId: createForm.item ? createForm.item.value : "",
                customer: createForm.customer || "",
                purchaseOrder: createForm.purchaseOrder || "",
                soQty: createForm.soQty || "",
                // ── production schedule (optional, editable in create) ──
                start_date: createForm.startDate || null,
                start_time: createForm.startTime || null,
                end_date: createForm.endDate || null,
                end_time: createForm.endTime || null,
                // ── order qty characteristics ──
                characteristics: (createForm.orderQtyChars || []).map(c => ({
                    characteristic: c.characteristic, value: c.value, calculation_type: c.calculation_type,
                })),
                components: createComponents.map(c => ({
                    item: c.item, item_category: c.item_category, product_code: c.product_code,
                    product_type: c.product_type, description: c.description, quantity: c.quantity,
                    uom: c.uom, assembly: c.assembly, valid_from: c.valid_from, valid_to: c.valid_to,
                    change_number: c.change_number,
                })),
                operations: createOperations.map(op => {
                    const activities = [];
                    if (op.setup !== undefined && op.setup !== null && Number(op.setup) !== 0) {
                        activities.push({ activity_type: "Setup", activity_value: op.setup, activity_unit: op.setup_uom || "Min" });
                    }
                    if (op.machine !== undefined && op.machine !== null && Number(op.machine) !== 0) {
                        activities.push({ activity_type: "Run", activity_value: op.machine, activity_unit: op.machine_uom || "Min" });
                    }
                    return {
                        process_id: op.process_id, process_name: op.process_name,
                        sub_process_id: op.sub_process_id, sub_process_name: op.sub_process_name,
                        workcenter_name: op.workcenter_name, workcenter_description: op.workcenter_description,
                        start_date: op.start_date || null, start_time: op.start_time || null,
                        end_date: op.end_date || null, end_time: op.end_time || null,
                        activities,
                    };
                }),
                createdBy: getCurrentUserId(),
            };

            const res = await apiFetch(`${PURCHASE_API}/planned-order/create`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));

            if (!res.ok || !data.success) {
                toast.error(data.error || "Failed to create Planned Order.");
                return;
            }

            toast.success(`Planned Order ${data.plannedOrderNo} created.`);
            setShowCreateModal(false);
            resetCreateForm();
            await fetchPlannedOrders({
                plannedOrderNo: selectedPlannedOrderNo,
                fromDate: selectedFromDate,
                toDate: selectedToDate,
                salesOrderNo: selectedSalesOrderNo,
            });
        } catch (e) {
            console.error("Failed to create Planned Order", e);
            toast.error("Failed to create Planned Order.");
        } finally {
            setCreating(false);
        }
    };

    // ══════════════════════════════════════════════════════════
    // CONVERT
    // ══════════════════════════════════════════════════════════
    const handleConvert = async () => {
        if (selectedRows.length === 0 || converting) return;

        const selectedPlannedOrders = selectedRows
            .map(idx => tabFiltered[idx])
            .filter(Boolean)
            .filter(o => !o.is_converted)
            .map(o => o.planned_order_number)
            .filter(Boolean);

        if (selectedPlannedOrders.length === 0) return;

        setConverting(true);
        setConvertResult(null);

        try {
            const res = await apiFetch(`${PURCHASE_API}/planned-order/convert`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    plannedOrderNumbers: selectedPlannedOrders,
                    convertedBy: getCurrentUserId(),
                }),
            });

            const data = await res.json();
            setConvertResult(data);

            if (data.success || (data.results || []).some(r => r.success)) {
                setSelectedRows([]);
                fetchPlannedOrders({
                    plannedOrderNo: selectedPlannedOrderNo,
                    fromDate: selectedFromDate,
                    toDate: selectedToDate,
                    salesOrderNo: selectedSalesOrderNo,
                });

                // Fire-and-forget: calculate & store planned cost (Semi Finished /
                // Finished good) for every Production Order just created by this
                // conversion. Doesn't block the conversion-result modal; failures
                // are logged only.
                (data.results || []).forEach(r => {
                    if (!r.success || !r.productionNo) return;
                    apiFetch(`${PURCHASE_API}/product-costing/calculate-planned-production-cost`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ productionOrderNumber: r.productionNo }),
                    }).catch(err => {
                        console.error(`Failed to calculate planned cost for PO ${r.productionNo}:`, err);
                    });
                });
            }
        } catch (err) {
            console.error("Convert failed", err);
            setConvertResult({
                success: false,
                results: selectedPlannedOrders.map(n => ({
                    plannedOrderNo: n,
                    success: false,
                    error: "Network error — could not reach server",
                })),
            });
        } finally {
            setConverting(false);
        }
    };

    const filteredOrders = orders.filter(o => {
        if (selectedProductType && o.product_type !== selectedProductType.value) return false;
        if (selectedProductCode?.value && String(o.product_code || "").trim() !== selectedProductCode.value) return false;
        if (selectedWorkcenter?.value) {
            const wcList = String(o.workcenter || "").split(",").map(w => w.trim());
            if (!wcList.includes(selectedWorkcenter.value)) return false;
        }
        if (listingFilters.construction?.value && String(o.construction || "").trim() !== listingFilters.construction.value) return false;
        if (listingFilters.code?.value && String(o.code || "").trim() !== listingFilters.code.value) return false;
        if (listingFilters.cableType?.value && String(o.cable_type || "").trim() !== listingFilters.cableType.value) return false;
        if (listingFilters.category?.value && String(o.product_category || "").trim() !== listingFilters.category.value) return false;
        if (listingFilters.noOfCores?.value && String(o.no_of_cores || "").trim() !== listingFilters.noOfCores.value) return false;
        if (listingFilters.color?.value && String(o.colour || "").trim() !== listingFilters.color.value) return false;
        if (listingFilters.nominalCrossSection?.value && String(o.nominal_cross_section || "").trim() !== listingFilters.nominalCrossSection.value) return false;
        if (listingFilters.referenceOfStandard?.value && String(o.reference_of_standard || "").trim() !== listingFilters.referenceOfStandard.value) return false;
        if (listingFilters.voltage?.value && String(o.voltage || "").trim() !== listingFilters.voltage.value) return false;
        return true;
    });

    const tabFiltered = filteredOrders.filter(o => {
        if (activeTab === "active") return !o.is_converted;
        if (activeTab === "posted") return !!o.is_converted;
        return true;
    });

    const parsedCustom = parseInt(customPageSize, 10);
    const pageSize =
        pageSizeOption === "all"
            ? Math.max(1, tabFiltered.length)
            : pageSizeOption === "custom"
                ? (parsedCustom > 0 ? parsedCustom : DEFAULT_PAGE_SIZE)
                : parseInt(pageSizeOption, 10);

    const handlePageSizeOptionChange = (value) => {
        setPageSizeOption(value);
        if (value === "custom" && !customPageSize) setCustomPageSize(String(DEFAULT_PAGE_SIZE));
        setCurrentPage(1);
        setSelectedRows([]); // selection is tracked by page-relative index, so reset it
    };

    const handleCustomPageSizeChange = (e) => {
        const digits = e.target.value.replace(/\D/g, "").slice(0, 5);
        setCustomPageSize(digits);
        setCurrentPage(1);
        setSelectedRows([]);
    };

    const totalPages = Math.max(1, Math.ceil(tabFiltered.length / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const pagedOrders = tabFiltered.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);

    const activeCount = filteredOrders.filter(o => !o.is_converted).length;
    const postedCount = filteredOrders.filter(o => !!o.is_converted).length;

    const smallSelectStyles = {
        control: (base, state) => ({
            ...base, minHeight: 32, height: 32, fontSize: 12,
            borderColor: state.isFocused ? "#64748b" : "#e2e8f0",
            borderRadius: 6, boxShadow: "none", cursor: "pointer",
            "&:hover": { borderColor: "#94a3b8" },
        }),
        valueContainer: (base) => ({ ...base, padding: "0 8px", height: 32, flexWrap: "nowrap" }),
        input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: 12 }),
        indicatorsContainer: (base) => ({ ...base, height: 32 }),
        dropdownIndicator: (base) => ({ ...base, padding: "0 4px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
        clearIndicator: (base) => ({ ...base, padding: "0 2px", color: "#94a3b8", svg: { width: 12, height: 12 } }),
        indicatorSeparator: () => ({ display: "none" }),
        placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: 12 }),
        singleValue: (base) => ({ ...base, fontSize: 12, color: "#1e293b" }),
        menu: (base) => ({ ...base, zIndex: 9999, fontSize: 12, borderRadius: 6, boxShadow: "0 4px 12px rgba(0,0,0,0.12)" }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
        option: (base, state) => ({
            ...base, fontSize: 12, padding: "6px 10px",
            backgroundColor: state.isSelected ? "#1e293b" : state.isFocused ? "#f1f5f9" : "#fff",
            color: state.isSelected ? "#fff" : "#334155",
        }),
    };

    const mLabel = { display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "3px", color: "#475569" };
    const mInput = { width: "100%", height: "30px", fontSize: "12px", padding: "3px 8px", border: "1px solid #dee2e6", borderRadius: 4, background: "#f1f5f9", color: "#1e293b", boxSizing: "border-box", outline: "none" };
    const soThStyle = {
        background: "#f8fafc",
        fontSize: 11,
        fontWeight: 600,
        padding: "7px 10px",
        borderBottom: "2px solid #e5e7eb",
        color: "#475569",
        whiteSpace: "nowrap",
        textAlign: "left",
    };
    const soTdStyle = {
        fontSize: 12,
        padding: "7px 10px",
        borderBottom: "1px solid #f1f5f9",
        color: "#1e293b",
        verticalAlign: "middle",
    };

    // A non-admin user with no DISPLAY/CHANGE/REVIEW/CREATE/CONVERT entry
    // for this module (either no role assigned at all, or a role that
    // simply doesn't include Planned Order) has canView/canCreate/canConvert
    // all false, per getModulePermissions above — same convention Enquiry
    // uses for SALES_INQUIRY. Admins always pass via the explicit
    // user.role === "admin" branch in getModulePermissions.
    if (!canSeePage) {
        return (
            <Layout>
                <ToastHost />
                <div style={{ padding: 40, textAlign: "center", color: "#64748b", fontSize: 14 }}>
                    You don't have access to Planned Order. Contact your administrator if you believe this is a mistake.
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <ToastHost />
            <style>{`
                .po-tab-toggle { display: inline-flex; background: #f1f5f9; border-radius: 8px; padding: 3px; gap: 2px; }
                .po-tab-toggle button {
                    border: none; background: transparent; border-radius: 6px;
                    padding: 5px 20px; font-size: 13px; font-weight: 600;
                    color: #64748b; cursor: pointer; transition: all 0.15s ease;
                    display: flex; align-items: center; gap: 6px;
                }
                .po-tab-toggle button.po-active-tab {
                    background: #fff; color: #1e293b; box-shadow: 0 1px 3px rgba(0,0,0,0.12);
                }
                .po-badge {
                    display: inline-flex; align-items: center; justify-content: center;
                    min-width: 18px; height: 18px; border-radius: 9px;
                    font-size: 11px; font-weight: 700; padding: 0 5px; line-height: 1;
                }
                .po-badge-active { background: #dbeafe; color: #1d4ed8; }
                .po-badge-posted { background: #dcfce7; color: #15803d; }
                .po-page-btn {
                    border: 1px solid #e2e8f0; background: #fff; border-radius: 6px;
                    width: 32px; height: 32px; display: flex; align-items: center;
                    justify-content: center; font-size: 13px; font-weight: 600;
                    cursor: pointer; color: #334155; transition: all 0.15s;
                }
                .po-page-btn:hover:not(:disabled) { background: #f1f5f9; }
                .po-page-btn.po-page-active { background: #1e293b; color: #fff; border-color: #1e293b; }
                .po-page-btn:disabled { opacity: 0.4; cursor: not-allowed; }
                .po-page-size-select {
                    height: 32px; border: 1px solid #e2e8f0; background: #fff; border-radius: 6px;
                    padding: 0 8px; font-size: 13px; font-weight: 600; color: #334155; outline: none;
                }
                .po-page-size-select:focus { border-color: #94a3b8; }
                .convert-overlay {
                    position: fixed; inset: 0; background: rgba(0,0,0,0.45);
                    z-index: 9999; display: flex; align-items: center; justify-content: center;
                }
                .convert-modal {
                    background: #fff; border-radius: 12px; padding: 28px 32px;
                    min-width: 420px; max-width: 560px; width: 100%;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
                }
                .view-modal {
                    background: #fff; border-radius: 8px; padding: 0;
                    width: 90%; max-width: 1400px; height: 90vh;
                    display: flex; flex-direction: column;
                    box-shadow: 0 8px 32px rgba(0,0,0,0.18);
                    overflow: hidden;
                }
                .hide-scrollbar::-webkit-scrollbar { display: none; }
                .convert-row-ok   { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
                .convert-row-fail { display: flex; align-items: flex-start; gap: 10px; padding: 8px 0; border-bottom: 1px solid #f1f5f9; }
                .convert-icon-ok   { color: #16a34a; font-size: 16px; margin-top: 1px; }
                .convert-icon-fail { color: #dc2626; font-size: 16px; margin-top: 1px; }
                @keyframes po-spin { to { transform: rotate(360deg); } }
            `}</style>

            {/* ── Convert result modal ── */}
            {convertResult && (
                <div className="convert-overlay" onClick={() => setConvertResult(null)}>
                    <div className="convert-modal" onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#1e293b" }}>Conversion Result</h3>
                                <div style={{ fontSize: 12, color: "#64748b", marginTop: 3 }}>
                                    {(convertResult.results || []).filter(r => r.success).length} of {(convertResult.results || []).length} converted successfully
                                </div>
                            </div>
                            <button onClick={() => setConvertResult(null)}
                                style={{ border: "none", background: "none", fontSize: 20, cursor: "pointer", color: "#94a3b8", lineHeight: 1, padding: 4 }}>×</button>
                        </div>
                        <div style={{ maxHeight: 360, overflowY: "auto" }}>
                            {(convertResult.results || []).map((r, i) => (
                                r.success ? (
                                    <div key={i} className="convert-row-ok">
                                        <span className="convert-icon-ok">✓</span>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>
                                                {r.plannedOrderNo}
                                                <span style={{ color: "#64748b", fontWeight: 400, marginLeft: 6 }}>→</span>
                                                <span style={{ color: "#2563eb", fontWeight: 700, marginLeft: 6 }}>{r.productionNo}</span>
                                            </div>
                                            <div style={{ fontSize: 12, color: "#16a34a", marginTop: 2 }}>Production order created successfully</div>
                                        </div>
                                    </div>
                                ) : (
                                    <div key={i} className="convert-row-fail">
                                        <span className="convert-icon-fail">✗</span>
                                        <div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#1e293b" }}>{r.plannedOrderNo}</div>
                                            <div style={{ fontSize: 12, color: "#dc2626", marginTop: 2 }}>{r.error || "Conversion failed"}</div>
                                        </div>
                                    </div>
                                )
                            ))}
                        </div>
                        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
                            <button onClick={() => setConvertResult(null)}
                                style={{ padding: "8px 22px", background: "#1e293b", color: "#fff", border: "none", borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: "pointer" }}>
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════════════
                VIEW MODAL — exact Production Order layout
            ══════════════════════════════════════════════════════════ */}
            {/* ══════════════════════════════════════════════════════════
                CREATE PLANNED ORDER MODAL (manual)
                Same 2 tabs as the view modal: Material Details, Machine Time.
                Components/operations auto-populate from BOM + routing once
                Product Code + Alt. BOM are picked — nothing is typed by hand.
               ══════════════════════════════════════════════════════════ */}
            {showCreateModal && PlannedOrderModal({ mode: "create" })}

            {showViewModal && PlannedOrderModal({ mode: "view" })}

            <div style={{ padding: "20px" }}>
                {/* ── Page Header ── */}
                <div style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    flexWrap: "wrap", gap: 12, marginBottom: 20,
                    background: "#fff", borderRadius: 10, padding: "16px 20px",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                }}>
                    <div>
                        <h2 style={{ fontSize: 22, fontWeight: 700, color: "#1e293b", margin: 0 }}>Planned Order</h2>
                        <div style={{ fontSize: 13, color: "#64748b", marginTop: 3 }}>
                            Manage and convert planned orders to production
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>

                        {activeTab === "active" && canConvert && (
                            <button
                                disabled={selectedRows.length === 0 || converting}
                                onClick={handleConvert}
                                style={{
                                    padding: "9px 20px",
                                    background: converting ? "#64748b" : "#1e293b",
                                    color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600,
                                    display: "flex", alignItems: "center", gap: 8,
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.18)", whiteSpace: "nowrap",
                                    transition: "background 0.2s",
                                    opacity: (selectedRows.length === 0 || converting) ? 0.55 : 1,
                                    cursor: (selectedRows.length === 0 || converting) ? "not-allowed" : "pointer",
                                    minWidth: 150, justifyContent: "center",
                                }}
                                onMouseEnter={e => { if (selectedRows.length > 0 && !converting) e.currentTarget.style.background = "#334155"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = converting ? "#64748b" : "#1e293b"; }}
                            >
                                {converting ? (
                                    <>
                                        <span style={{
                                            display: "inline-block", width: 15, height: 15,
                                            border: "2px solid rgba(255,255,255,0.4)",
                                            borderTopColor: "#fff", borderRadius: "50%",
                                            animation: "po-spin 0.7s linear infinite",
                                        }} />
                                        Converting…
                                    </>
                                ) : (
                                    <>
                                        <span style={{ fontSize: 18, lineHeight: 1, fontWeight: 400 }}>⇄</span>
                                        Convert{selectedRows.length > 0 ? ` (${selectedRows.length})` : ""}
                                    </>
                                )}
                            </button>
                        )}

                        {canCreate && (
                            <button
                                onClick={openCreateModal}
                                style={{
                                    padding: "9px 20px", background: "#0b0b0b", color: "#fff", border: "none",
                                    borderRadius: 8, fontSize: 14, fontWeight: 600, display: "flex", alignItems: "center",
                                    gap: 8, boxShadow: "0 2px 8px rgba(0,0,0,0.18)", whiteSpace: "nowrap", cursor: "pointer",
                                }}
                            >
                                <span style={{ fontSize: 16, lineHeight: 1 }}>+</span>
                                Create
                            </button>
                        )}

                        <button
                            onClick={() => {
                                setManualExists(null);
                                setManualFile(null);
                                setShowManual(true);
                                apiFetch(USER_MANUAL_LIST_API, { headers: authHeaders() })
                                    .then((res) => res.json())
                                    .then((data) => {
                                        const norm = (v) => String(v || "").trim().toLowerCase();
                                        const match = (data?.data || []).find((m) =>
                                            norm(m.module_name) === norm(USER_MANUAL_SCOPE.moduleName) &&
                                            norm(m.process_name) === norm(USER_MANUAL_SCOPE.process) &&
                                            norm(m.sub_process_name) === norm(USER_MANUAL_SCOPE.subProcess)
                                        );
                                        if (match && match.drive_link) {
                                            setManualFile(match);
                                            setManualExists(true);
                                        } else {
                                            setManualExists(false);
                                        }
                                    })
                                    .catch(() => setManualExists(false));
                            }}
                            title="View User Manual"
                            style={{
                                background: "#ffffff",
                                border: "1px solid #e2e8f0",
                                borderRadius: "10px",
                                padding: "10px 14px",
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer",
                                color: "#3b82f6",
                                fontSize: "14px",
                                fontWeight: "600",
                                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#3b82f6"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e2e8f0"; }}
                        >
                            <FileText size={18} />
                            User Manual
                        </button>

                    </div>
                </div>

                {/* --- User Manual Popup Modal --- */}
                {showManual && (
                    <div
                        onClick={() => setShowManual(false)}
                        style={{
                            position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
                            backgroundColor: "rgba(15, 23, 42, 0.6)", display: "flex",
                            alignItems: "center", justifyContent: "center", zIndex: 1100,
                        }}
                    >
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: "#ffffff",
                                borderRadius: "12px",
                                width: "90vw",
                                height: "97vh",
                                display: "flex",
                                flexDirection: "column",
                                overflow: "hidden",
                                boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)",
                            }}
                        >
                            <div style={{
                                display: "flex", justifyContent: "space-between", alignItems: "center",
                                padding: "14px 20px", borderBottom: "1px solid #e2e8f0",
                            }}>
                                <h4 style={{ margin: 0, color: "#0f172a", fontSize: "16px", fontWeight: "600" }}>
                                    User Manual
                                </h4>
                                <button
                                    onClick={() => setShowManual(false)}
                                    style={{
                                        border: "none", background: "#f1f5f9", borderRadius: "8px",
                                        width: "32px", height: "32px", cursor: "pointer", fontSize: "18px",
                                        color: "#475569", lineHeight: 1,
                                    }}
                                    title="Close"
                                >
                                    ×
                                </button>
                            </div>

                            {manualExists === null && (
                                <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "14px" }}>
                                    Checking for user manual...
                                </div>
                            )}

                            {manualExists === false && (
                                <div style={{
                                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                                    justifyContent: "center", color: "#64748b", gap: "10px", padding: "20px", textAlign: "center"
                                }}>
                                    <FileText size={40} color="#cbd5e1" />
                                    <div style={{ fontSize: "15px", fontWeight: 600, color: "#334155" }}>
                                        User Manual Not Available
                                    </div>
                                    <div style={{ fontSize: "13px", color: "#94a3b8", maxWidth: "320px" }}>
                                        The user manual for this module hasn't been uploaded yet. Please check back later or contact your administrator.
                                    </div>
                                </div>
                            )}

                            {manualExists === true && manualFile && (() => {
                                const MANUAL_SCALE = 1.25;
                                return (
                                    <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
                                        <iframe
                                            src={manualFile.drive_link.replace("/view", "/preview")}
                                            title={manualFile.file_name || "User Manual"}
                                            allow="autoplay"
                                            style={{
                                                border: "none",
                                                width: `${100 / MANUAL_SCALE}%`,
                                                height: `${100 / MANUAL_SCALE}%`,
                                                transform: `scale(${MANUAL_SCALE})`,
                                                transformOrigin: "top left",
                                            }}
                                        />
                                    </div>
                                );
                            })()}
                        </div>
                    </div>
                )}

                {/* ── FILTERS + TABS + TABLE: hidden for CREATE-only (add-only) roles,
                     which have can_view = 0 and no visibility into existing records.
                     Convert-only roles have can_view = 0 too but still need the list
                     to pick rows from, so this gates on (canView || canConvert) —
                     same idea as Enquiry's canView-only gate, extended for Convert. */}
                {(canView || canConvert) && (
                <>
                {/* ── Filters ── */}
                <div style={{ background: "#fff", borderRadius: 10, padding: "14px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.08)", marginBottom: 20 }}>
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Product Type</label>
                            <Select
                                options={poFilterMasterData.productTypes}
                                value={selectedProductType}
                                onChange={(opt) => { setSelectedProductType(opt); setCurrentPage(1); }}
                                isClearable placeholder="Select Product Type"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={smallSelectStyles}
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", marginBottom: 10 }}>
                        {[
                            { key: "construction", opts: poFilterMasterData.constructions, label: "Construction" },
                            { key: "code", opts: poFilterMasterData.codes, label: "Code" },
                            { key: "cableType", opts: poFilterMasterData.cableTypes, label: "Cable Type" },
                            { key: "category", opts: poFilterMasterData.categories, label: "Category" },
                            { key: "noOfCores", opts: poFilterMasterData.cores, label: "Cores" },
                            { key: "color", opts: poFilterMasterData.colours, label: "Color" },
                            { key: "nominalCrossSection", opts: poFilterMasterData.nominalCrossSections, label: "Cross Section" },
                            { key: "referenceOfStandard", opts: poFilterMasterData.referenceOdStandards, label: "Std. Ref" },
                            { key: "voltage", opts: poFilterMasterData.voltages, label: "Voltage" },
                        ].map(({ key, opts, label }) => (
                            <div key={key} style={{ flex: "1 1 0", minWidth: 100 }}>
                                <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>{label}</label>
                                <Select
                                    placeholder={label} isClearable
                                    options={getPoFilterOptions(opts)}
                                    value={listingFilters[key]}
                                    onChange={opt => handleListingFilter(key, opt)}
                                    menuPortalTarget={document.body} menuPosition="fixed"
                                    styles={smallSelectStyles}
                                />
                            </div>
                        ))}
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Sales Order No.</label>
                            <Select
                                options={salesOrderNumbers.map(s => ({ value: s.sales_order_no, label: s.sales_order_no }))}
                                value={selectedSalesOrderNo ? { value: selectedSalesOrderNo, label: selectedSalesOrderNo } : null}
                                onChange={handleSalesOrderNoChange}
                                isClearable placeholder="Sales Order No."
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={smallSelectStyles}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Planned Order</label>
                            <Select
                                options={plannedOrderNumbers.map(p => ({ value: p.planned_order_number, label: p.planned_order_number }))}
                                value={selectedPlannedOrderNo ? { value: selectedPlannedOrderNo, label: selectedPlannedOrderNo } : null}
                                onChange={handlePlannedOrderNoChange}
                                isClearable placeholder="Planned Order"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={smallSelectStyles}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Product Code</label>
                            <Select
                                options={productCodeMasterOptions}
                                value={selectedProductCode}
                                onChange={(opt) => { setSelectedProductCode(opt); setCurrentPage(1); }}
                                isClearable placeholder="Product Code"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={smallSelectStyles}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>Workcenter</label>
                            <Select
                                options={workcenterMasterOptions}
                                value={selectedWorkcenter}
                                onChange={(opt) => { setSelectedWorkcenter(opt); setCurrentPage(1); }}
                                isClearable placeholder="Workcenter"
                                menuPortalTarget={document.body} menuPosition="fixed"
                                styles={smallSelectStyles}
                            />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>From</label>
                            <DateField value={selectedFromDate} onChange={(v) => handleFromDateChange({ target: { value: v } })} />
                        </div>
                        <div style={{ flex: "1 1 0", minWidth: 150 }}>
                            <label style={{ fontSize: 11, fontWeight: 600, color: "#64748b", display: "block", marginBottom: 4 }}>To</label>
                            <DateField value={selectedToDate} onChange={(v) => handleToDateChange({ target: { value: v } })} disabled={!selectedFromDate} />
                        </div>
                        {hasActiveFilters && (
                            <button
                                type="button"
                                onClick={handleClearFilters}
                                title="Clear all filters"
                                style={{ height: 32, padding: "0 14px", fontSize: 12, fontWeight: 600, color: "#dc2626", background: "#fff", border: "1.5px solid #fecaca", borderRadius: 6, cursor: "pointer", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 6 }}
                                onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; }}
                                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
                            >
                                <span style={{ fontSize: 14, lineHeight: 1 }}>&times;</span> Clear Filters
                            </button>
                        )}
                    </div>
                </div>

                {/* ── Active / Posted Toggle ── */}
                <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 14 }}>
                    <div className="po-tab-toggle">
                        <button className={activeTab === "active" ? "po-active-tab" : ""} onClick={() => setActiveTab("active")}>
                            Active
                            {activeCount > 0 && <span className="po-badge po-badge-active">{activeCount}</span>}
                        </button>
                        <button className={activeTab === "posted" ? "po-active-tab" : ""} onClick={() => setActiveTab("posted")}>
                            Posted
                            {postedCount > 0 && <span className="po-badge po-badge-posted">{postedCount}</span>}
                        </button>
                    </div>
                </div>

                {/* ── Table ── */}
                <div style={{ background: "#ffffff", borderRadius: 10, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
                    <div style={{ overflowX: "auto", width: "100%" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 700 }}>
                            <thead>
                                <tr style={{ background: "#f8fafc" }}>
                                    {activeTab === "active" && canConvert && (
                                        <th style={{ ...styles.erpTh, width: 40, textAlign: "center" }}>
                                            <input
                                                type="checkbox"
                                                onChange={handleSelectAll}
                                                checked={
                                                    pagedOrders.filter(o => !o.is_converted).length > 0 &&
                                                    pagedOrders
                                                        .filter(o => !o.is_converted)
                                                        .every((_, i) => {
                                                            // const globalIdx = (safeCurrentPage - 1) * pageSize +
                                                            pagedOrders.findIndex((o2, j) => !o2.is_converted && j >= i * 0);
                                                            return selectedRows.includes((safeCurrentPage - 1) * pageSize + pagedOrders.indexOf(pagedOrders.filter(o => !o.is_converted)[i]));
                                                        })
                                                }
                                                ref={el => {
                                                    if (el) {
                                                        const selectableOnPage = pagedOrders.filter(o => !o.is_converted);
                                                        const selectedOnPage = pagedOrders
                                                            .map((o, i) => ({ o, globalIdx: (safeCurrentPage - 1) * pageSize + i }))
                                                            .filter(({ o, globalIdx }) => !o.is_converted && selectedRows.includes(globalIdx));
                                                        el.indeterminate = selectedOnPage.length > 0 && selectedOnPage.length < selectableOnPage.length;
                                                        el.checked = selectableOnPage.length > 0 && selectedOnPage.length === selectableOnPage.length;
                                                    }
                                                }}
                                                style={{ cursor: "pointer", width: 14, height: 14 }}
                                            />
                                        </th>
                                    )}
                                    <th style={styles.erpTh}>Planned Order</th>
                                    {/* Moved Production Order here */}
                                    {activeTab === "posted" && (
                                        <th style={styles.erpTh}>Production Order</th>
                                    )}
                                    <th style={styles.erpTh}>Product Code</th>
                                    <th style={styles.erpTh}>Description</th>
                                    <th style={styles.erpTh}>Workcenter</th>
                                    <th style={{ ...styles.erpTh, textAlign: "right" }}>Order Qty</th>
                                    <th style={styles.erpTh}>UOM</th>
                                    <th style={styles.erpTh}>Start Date</th>
                                    <th style={styles.erpTh}>End Date</th>
                                    <th style={{ ...styles.erpTh, textAlign: "center" }}>Action</th>
                                </tr>
                            </thead>

                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan={10} style={{ ...styles.erpTd, textAlign: "center", color: "#94a3b8", padding: "40px 0", fontSize: 13 }}>
                                            Loading planned orders…
                                        </td>
                                    </tr>
                                ) : pagedOrders.length === 0 ? (
                                    <tr>
                                        <td colSpan={10} style={{ ...styles.erpTd, textAlign: "center", color: "#94a3b8", padding: "40px 0", fontSize: 13 }}>
                                            {activeTab === "posted"
                                                ? "No converted planned orders yet."
                                                : "No planned orders found."}
                                        </td>
                                    </tr>
                                ) : (
                                    pagedOrders.map((o, i) => {
                                        const globalIdx = (safeCurrentPage - 1) * pageSize + i;
                                        const isSelected = selectedRows.includes(globalIdx);
                                        const isConverted = !!o.is_converted;

                                        return (
                                            <tr
                                                key={globalIdx}
                                                style={{
                                                    background: isSelected
                                                        ? "#eff6ff"
                                                        : isConverted
                                                            ? "#f8fffe"
                                                            : i % 2 === 0 ? "#fff" : "#fafbfc",
                                                    opacity: isConverted && activeTab === "active" ? 0.6 : 1,
                                                }}
                                                onMouseEnter={e => { if (!isSelected && !isConverted) e.currentTarget.style.background = "#f1f5f9"; }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.background = isSelected
                                                        ? "#eff6ff"
                                                        : isConverted ? "#f8fffe"
                                                            : i % 2 === 0 ? "#fff" : "#fafbfc";
                                                }}
                                            >
                                                {activeTab === "active" && canConvert && (
                                                    <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                                        {isConverted ? (
                                                            <span style={{ color: "#16a34a", fontSize: 14 }} title="Already converted to Production Order">✓</span>
                                                        ) : (
                                                            <input
                                                                type="checkbox"
                                                                checked={isSelected}
                                                                onChange={() => handleRowSelect(globalIdx, isConverted)}
                                                                style={{ cursor: "pointer", width: 14, height: 14 }}
                                                            />
                                                        )}
                                                    </td>
                                                )}

                                                {/* Planned Order with Modal Hyperlink */}
                                                <td style={{ ...styles.erpTd, fontWeight: 600, color: "#1e293b" }}>
                                                    <span
                                                        onClick={() => openViewModal(o.planned_order_number)}
                                                        style={{ color: "#2563eb", cursor: "pointer", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                        onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                        onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                    >
                                                        {o.planned_order_number || ""}
                                                    </span>
                                                </td>

                                                {/* Production Order Column (Moved next to Planned Order) */}
                                                {activeTab === "posted" && (
                                                    <td style={{ ...styles.erpTd, fontWeight: 600 }}>
                                                        {o.converted_production_no ? (
                                                            <Link
                                                                to={`/cable/production-order?pod=${encodeURIComponent(o.converted_production_no)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                                style={{ color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                                onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                                onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                            >
                                                                {o.converted_production_no}
                                                            </Link>
                                                        ) : ""}
                                                    </td>
                                                )}

                                                {/* Product Code Column */}
                                                <td style={{ ...styles.erpTd, fontWeight: 500 }}>
                                                    {o.product_code ? (
                                                        <Link
                                                            to={`/cable/product/product-code?view=${encodeURIComponent(o.product_code)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                            style={{ color: "#2563eb", textDecoration: "underline", textUnderlineOffset: 3 }}
                                                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                        >
                                                            {o.product_code}
                                                        </Link>
                                                    ) : ""}
                                                </td>

                                                {/* Description Column */}
                                                <td style={{ ...styles.erpTd, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                                    title={o.description || ""}>
                                                    {o.description || ""}
                                                </td>

                                                {/* Workcenter with Navigation Hyperlink */}
                                                <td style={styles.erpTd}>
                                                    {o.workcenter ? (
                                                        <Link
                                                            to={`/cable/master-data/workcenter?view=${encodeURIComponent(o.workcenter)}&returnTo=${encodeURIComponent(location.pathname + location.search)}`}
                                                            style={{ color: "#2563eb", fontWeight: 500, textDecoration: "underline", textUnderlineOffset: 3 }}
                                                            onMouseEnter={e => e.currentTarget.style.color = "#1d4ed8"}
                                                            onMouseLeave={e => e.currentTarget.style.color = "#2563eb"}
                                                        >
                                                            {o.workcenter}
                                                        </Link>
                                                    ) : "—"}
                                                </td>

                                                {/* Remove the old Production Order <td> that used to be right before the Action column */}

                                                <td style={{ ...styles.erpTd, textAlign: "right", fontWeight: 600 }}>{o.order_qty || ""}</td>
                                                <td style={styles.erpTd}>{o.uom || ""}</td>
                                                <td style={styles.erpTd}>{formatDate(o.start_date)}</td>
                                                <td style={styles.erpTd}>{formatDate(o.end_date)}</td>

                                                <td style={{ ...styles.erpTd, textAlign: "center" }}>
                                                    <div style={{ display: "flex", gap: 6, justifyContent: "center", alignItems: "center" }}>
                                                        <button
                                                            style={{
                                                                padding: "5px 14px", fontSize: 12, fontWeight: 600,
                                                                border: "1.5px solid #cbd5e1", borderRadius: 6,
                                                                background: "#fff", color: "#334155",
                                                                cursor: "pointer", transition: "all 0.15s",
                                                            }}
                                                            onMouseEnter={e => { e.currentTarget.style.background = "#1e293b"; e.currentTarget.style.color = "#fff"; e.currentTarget.style.borderColor = "#1e293b"; }}
                                                            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#334155"; e.currentTarget.style.borderColor = "#cbd5e1"; }}
                                                            onClick={() => openViewModal(o.planned_order_number)}
                                                        >
                                                            View
                                                        </button>
                                                        {canEdit && (
                                                            <button
                                                                type="button"
                                                                title="Delete Planned Order"
                                                                disabled={deletingOrderNo === o.planned_order_number}
                                                                onClick={() => handleDeletePlannedOrder(o.planned_order_number)}
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    justifyContent: "center",
                                                                    width: 30,
                                                                    height: 30,
                                                                    padding: 0,
                                                                    background: deletingOrderNo === o.planned_order_number ? "#fee2e2" : "#fff",
                                                                    border: "1.5px solid #fecaca",
                                                                    borderRadius: 6,
                                                                    cursor: deletingOrderNo === o.planned_order_number ? "not-allowed" : "pointer",
                                                                }}
                                                            >
                                                                {deletingOrderNo === o.planned_order_number ? (
                                                                    <span style={{ fontSize: 11, color: "#dc2626" }}>...</span>
                                                                ) : (
                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <polyline points="3 6 5 6 21 6"></polyline>
                                                                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"></path>
                                                                        <path d="M10 11v6"></path>
                                                                        <path d="M14 11v6"></path>
                                                                        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"></path>
                                                                    </svg>
                                                                )}
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    {tabFiltered.length > 0 && (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9", background: "#fff", flexWrap: "wrap", gap: 8 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
                                <span style={{ fontSize: 13, color: "#64748b" }}>
                                    Showing {(safeCurrentPage - 1) * pageSize + 1}–{Math.min(safeCurrentPage * pageSize, tabFiltered.length)} of {tabFiltered.length} orders
                                </span>
                                <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "#64748b" }}>
                                    Rows per page
                                    <select
                                        className="po-page-size-select"
                                        value={pageSizeOption}
                                        onChange={(e) => handlePageSizeOptionChange(e.target.value)}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((opt) => (
                                            <option key={opt} value={opt}>
                                                {opt === "all" ? "All" : opt === "custom" ? "Custom" : opt}
                                            </option>
                                        ))}
                                    </select>
                                </label>
                                {pageSizeOption === "custom" && (
                                    <input
                                        className="po-page-size-select"
                                        type="text"
                                        inputMode="numeric"
                                        value={customPageSize}
                                        onChange={handleCustomPageSizeChange}
                                        onBlur={() => { if (!(parseInt(customPageSize, 10) > 0)) setCustomPageSize(String(DEFAULT_PAGE_SIZE)); }}
                                        placeholder="Rows"
                                        title="Enter number of rows per page"
                                        style={{ width: 72 }}
                                    />
                                )}
                            </div>
                            <div style={{ display: pageSizeOption === "all" || totalPages === 1 ? "none" : "flex", gap: 4, alignItems: "center" }}>
                                <button className="po-page-btn" disabled={safeCurrentPage === 1} onClick={() => setCurrentPage(1)} title="First">«</button>
                                <button className="po-page-btn" disabled={safeCurrentPage === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))} title="Previous">‹</button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter(p => p === 1 || p === totalPages || Math.abs(p - safeCurrentPage) <= 1)
                                    .reduce((acc, p, idx, arr) => {
                                        if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, idx) =>
                                        p === "..." ? (
                                            <span key={`ellipsis-${idx}`} style={{ fontSize: 13, color: "#94a3b8", padding: "0 4px" }}>…</span>
                                        ) : (
                                            <button key={p}
                                                className={`po-page-btn${safeCurrentPage === p ? " po-page-active" : ""}`}
                                                onClick={() => setCurrentPage(p)}>{p}</button>
                                        )
                                    )
                                }

                                <button className="po-page-btn" disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} title="Next">›</button>
                                <button className="po-page-btn" disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage(totalPages)} title="Last">»</button>
                            </div>
                        </div>
                    )}
                </div>
                </>
                )}

            </div>
        </Layout>
    );
};

const styles = {
    erpTh: {
        background: "#f8fafc", fontSize: 13, fontWeight: 600,
        padding: "10px 12px", borderBottom: "1px solid #e5e7eb",
        color: "#1e293b", whiteSpace: "nowrap",
    },
    erpTd: {
        fontSize: 13, padding: "10px 12px",
        borderBottom: "1px solid #f1f5f9",
        color: "#334155", verticalAlign: "middle",
    },
};

export default PlannedOrder;